/**
 * ============================================
 * COMPLETE WORKING CONVERSATION MEMORY SERVICE
 * Replace your entire ConversationMemorySystem.js with this
 * ============================================
 */

import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  increment,
  Timestamp,
  writeBatch,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../../../firebase/firebase'; 
import { SecureAPI } from '../config.js';
export class ConversationMemoryService {
  constructor(firestoreService, userId, azureConfig = null) {
    this.firestoreService = firestoreService;
    this.userId = userId;
    this.db = db;
    
    this.currentUser = null;
    this.preptuneUserId = null;
    this._initialized = false;
    
    // ✅ Store Azure config
    this.azureConfig = azureConfig;
    
    this.extractionThresholds = {
      personalFact: 0.70,
      emotion: 0.60,
      goal: 0.65,
      preference: 0.60,
      habit: 0.65
    };

    if (firestoreService?.isReady()) {
      this.initialize();
    }
  }
  
  async initialize() {
    if (this._initialized) {
      console.log('✅ Memory service already initialized');
      return true;
    }

    try {
      if (!this.firestoreService?.isReady()) {
        console.warn('⏳ Firestore not ready yet, waiting...');
        await this.waitForFirestore();
      }

      const userData = await this.firestoreService.getUserData();
      
      this.currentUser = this.firestoreService.currentUser;
      this.preptuneUserId = userData.preptuneUserId;
      this._initialized = true;

      console.log('✅ Memory service initialized:', {
        userId: this.userId,
        preptuneUserId: this.preptuneUserId
      });

      return true;
    } catch (error) {
      console.error('❌ Memory service initialization failed:', error);
      return false;
    }
  }

  async waitForFirestore(maxAttempts = 30) {
    for (let i = 0; i < maxAttempts; i++) {
      if (this.firestoreService?.isReady()) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    throw new Error('Firestore not ready after timeout');
  }

  isReady() {
    return !!(
      this._initialized &&
      this.db && 
      this.currentUser && 
      this.preptuneUserId &&
      this.firestoreService?.isReady()
    );
  }

  // ============================================
  // MEMORY EXTRACTION
  // ============================================
async extractMemoriesFromConversation(messages) {
  if (!messages || messages.length === 0) return [];

  try {
    const userMessages = messages.filter(m => m.type === 'user' && m.content);
    if (userMessages.length === 0) return [];

    const conversationText = userMessages.map(m => m.content).join('\n');
    console.log('🧠 Extracting memories via LLM...');

    // ✅ USE SecureAPI
const data = await SecureAPI.callAzureOpenAI(
  [
    {
      role: 'system',
      content: `Extract ONLY meaningful facts about the person from this conversation.

What to extract:
- Personal info (name, age, location, occupation, education)
- Preferences (likes, dislikes, favorites, interests)
- Goals and aspirations
- Habits and routines
- Upcoming events with dates/times
- Emotional states tied to specific contexts
- Relationships and social connections
- Skills and expertise
- Challenges or problems they're facing

What NOT to extract:
- Commands to you ("create a podcast")
- Questions they asked
- Greetings or small talk
- Generic responses

⚠️ CRITICAL RULES:
1. ALWAYS use "You" perspective: "You like soccer" NOT "User likes soccer"
2. Be specific and factual
3. One clear fact per memory
4. If nothing meaningful, return empty array
5. ✅ ALWAYS include ALL fields: memory, type, category, confidence, importance

Return ONLY valid JSON array:
[
  {
    "memory": "You're a student at NYU studying computer science",
    "type": "personalFact",
    "category": "education",
    "confidence": 0.95,
    "importance": "high"
  }
]

If no memories, return: []`
    },
    {
      role: 'user',
      content: conversationText
    }
  ],
  {
    temperature: 0.3,
    max_tokens: 800,
    tools: undefined // Explicitly no tools for memory extraction
  }
);

    let content = data.choices[0].message.content.trim();
    
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      const memories = JSON.parse(jsonMatch[0]);
      console.log(`✅ Extracted ${memories.length} memories`);
      
      const validated = memories
        .filter(m => {
          if (!m.memory) return false;
          const startsCorrectly = /^(you|your)\b/i.test(m.memory.trim());
          if (!startsCorrectly) {
            console.warn(`⚠️ Skipping incorrect perspective: "${m.memory}"`);
          }
          return startsCorrectly;
        })
        .map(m => ({
          memory: m.memory,
          type: m.type || 'general',
          category: m.category || 'general',
          confidence: m.confidence || 0.7,
          importance: m.importance || 'medium',
          tags: m.tags || []
        }));
      
      console.log(`✅ ${validated.length} memories passed validation`);
      return validated;
    }

    return [];

  } catch (error) {
    console.error('❌ Memory extraction failed:', error);
    return [];
  }
}
  async extractPersonalFacts(text) {
    const facts = [];

    const namePatterns = [
      /(?:my name is|i'm|i am|call me)\s+([A-Z][a-z]{2,15})(?:\s|,|\.)/gi
    ];

    namePatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        const name = match[1].trim();
        if (!['Going', 'Fine', 'Good', 'Happy'].includes(name)) {
          facts.push({
            type: 'personalFact',
            category: 'name',
            memory: `User's name is ${name}`,
            value: name,
            confidence: 0.90,
            importance: 'high',
            topic: 'identity',
            tags: ['name', 'identity', name.toLowerCase()]
          });
        }
      }
    });

    return facts;
  }

  async extractEmotions(text) {
    const emotions = [];
    const lowerText = text.toLowerCase();

    const emotionKeywords = {
      stressed: { sentiment: 'negative', intensity: 0.85 },
      anxious: { sentiment: 'negative', intensity: 0.85 },
      happy: { sentiment: 'positive', intensity: 0.80 },
      excited: { sentiment: 'positive', intensity: 0.85 }
    };

    Object.entries(emotionKeywords).forEach(([emotion, data]) => {
      const regex = new RegExp(`\\b(${emotion})\\b`, 'gi');
      const matches = lowerText.match(regex);

      if (matches) {
        emotions.push({
          type: 'emotion',
          category: 'emotional_state',
          memory: `User feels ${emotion}`,
          value: emotion,
          sentiment: data.sentiment,
          confidence: 0.75,
          importance: 'medium',
          topic: 'emotions',
          tags: ['emotion', emotion]
        });
      }
    });

    return emotions;
  }

  async extractGoals(text) {
    const goals = [];

    const goalPatterns = [
      /(?:i want to|my goal is to)\s+([^.!?]{5,100})/gi
    ];

    goalPatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        const goal = match[1].trim();
        
        if (goal.length > 5) {
          goals.push({
            type: 'goal',
            category: 'aspiration',
            memory: `User wants to ${goal}`,
            value: goal,
            confidence: 0.82,
            importance: 'high',
            topic: 'goals',
            tags: ['goal']
          });
        }
      }
    });

    return goals;
  }

  async extractPreferences(text) {
    const preferences = [];

    const prefPatterns = [
      /(?:i (?:love|like|prefer|hate|dislike))\s+([^.!?]{3,60})/gi
    ];

    prefPatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        const pref = match[1].trim();
        const isPositive = !match[0].toLowerCase().match(/hate|dislike/);
        
        if (pref.length > 2) {
          preferences.push({
            type: 'preference',
            category: 'likes_dislikes',
            memory: `User ${isPositive ? 'likes' : 'dislikes'} ${pref}`,
            value: pref,
            sentiment: isPositive ? 'positive' : 'negative',
            confidence: 0.75,
            importance: 'medium',
            topic: 'preferences',
            tags: ['preference']
          });
        }
      }
    });

    return preferences;
  }

  async extractHabits(text) {
    const habits = [];

    const habitPatterns = [
      /(?:i (?:usually|often|always))\s+([^.!?]{5,80})/gi
    ];

    habitPatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        const habit = match[1].trim();
        
        if (habit.length > 5) {
          habits.push({
            type: 'habit',
            category: 'routine',
            memory: `User usually ${habit}`,
            value: habit,
            confidence: 0.72,
            importance: 'medium',
            topic: 'habits',
            tags: ['habit']
          });
        }
      }
    });

    return habits;
  }

  deduplicateMemories(memories) {
    const memoryMap = new Map();

    memories.forEach(memory => {
      const key = `${memory.type}:${(memory.value || '').toLowerCase()}`;

      if (!memoryMap.has(key)) {
        memoryMap.set(key, { ...memory, mentions: 1 });
      } else {
        const existing = memoryMap.get(key);
        existing.confidence = Math.min(existing.confidence + 0.05, 0.98);
        existing.mentions = (existing.mentions || 1) + 1;
      }
    });

    return Array.from(memoryMap.values());
  }

/**
 * Check if similar memory already exists
 */
async checkMemoryDuplicate(memoryText) {
  try {
    if (!this.isReady()) return false;

    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    // Simple duplicate check - exact match
    const q = query(
      memoryRef,
      where('memory', '==', memoryText)
    );

    const snapshot = await getDocs(q);
    return snapshot.size > 0;

  } catch (error) {
    console.error('Error checking duplicates:', error);
    return false;
  }
}
 /**
   * ✅ FIXED: Save memories with proper batch handling
   */
async saveMemoriesToFirestore(memories) {
  if (!memories || memories.length === 0) return [];
  
  if (!this._initialized) {
    await this.initialize();
  }

  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready, skipping save');
    return [];
  }

  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    const savedIds = [];

    for (const memory of memories) {
      try {
        const isDuplicate = await this.checkMemoryDuplicate(memory.memory);
        if (isDuplicate) {
          console.log(`⏭️ Skipping duplicate: ${memory.memory.substring(0, 30)}...`);
          continue;
        }

        const azureConfig = this.azureConfig || null;

        // ✅ FIX: Provide defaults for ALL fields
        let finalImportance = memory.importance || 'medium';
        if (!memory.importance) {
          finalImportance = await this.determineMemoryImportance(memory.memory, azureConfig);
        }

        const timeframe = await this.classifyMemoryTimeframe(
          { ...memory, importance: finalImportance },
          azureConfig
        );

        let expiresAt = null;
        if (timeframe === 'SHORT_TERM') {
          const eventDate = this.extractEventDate(memory.memory);
          
          if (eventDate) {
            const expiryDate = new Date(eventDate);
            expiryDate.setDate(expiryDate.getDate() + 1);
            expiresAt = Timestamp.fromDate(expiryDate);
            console.log(`📅 Event-based expiration: ${expiryDate.toDateString()}`);
          } else {
            const expiry = new Date();
            expiry.setDate(expiry.getDate() + 30);
            expiresAt = Timestamp.fromDate(expiry);
            console.log(`⏱️ 30-day expiration set`);
          }
        }

        // ✅ NEW: Provide defaults for all fields to prevent undefined errors
        const memoryDoc = {
          preptuneUserId: this.preptuneUserId,
          userId: this.currentUser.uid,
          memory: memory.memory,
          type: memory.type || 'general',                    // ✅ Default
          category: memory.category || 'general',            // ✅ Default
          value: memory.value || '',                         // ✅ Default
          topic: memory.topic || 'general',                  // ✅ Default
          confidence: memory.confidence || 0.7,              // ✅ Default
          importance: finalImportance,
          tags: Array.isArray(memory.tags) ? memory.tags : [], // ✅ Default
          
          timeframe: timeframe,
          expiresAt: expiresAt,
          isActive: true,
          
          mentionedAt: serverTimestamp(),
          createdAt: serverTimestamp()
        };

        const docRef = await addDoc(memoryRef, memoryDoc);
        savedIds.push(docRef.id);
        
        console.log(`✅ Saved memory: ${memory.memory.substring(0, 40)}... (${timeframe})`);
      } catch (error) {
        console.error(`❌ Failed to save individual memory:`, error.message);
      }
    }

    console.log(`✅ Total saved: ${savedIds.length}/${memories.length} memories`);
    return savedIds;
  } catch (error) {
    console.error('❌ Failed to save memories:', error);
    return [];
  }
}
  // ============================================
  // RETRIEVAL
  // ============================================

/**
 * ✅ CHECK: Handle virgin/empty memory state
 */
async getRecentMemories(limitCount = 10, daysBack = 7) {
  if (!this._initialized) {
    await this.initialize();
  }

  if (!this.isReady()) {
    console.log('⏭️ Memory service not ready - returning empty array');
    return [];
  }

  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysBack);
    
    const q = query(
      memoryRef,
      where('mentionedAt', '>=', Timestamp.fromDate(cutoffDate)),
      orderBy('mentionedAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    
    // ✅ CRITICAL: Handle empty collection (new users)
    if (querySnapshot.empty) {
      console.log('📭 No memories exist yet for this user (new user or first chat)');
      return [];
    }
    
    const memories = [];
    querySnapshot.forEach((doc) => {
      memories.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`🧠 Retrieved ${memories.length} recent memories`);
    return memories;
    
  } catch (error) {
    // ✅ Graceful error handling for missing collections
    if (error.code === 'not-found' || error.message.includes('no documents')) {
      console.log('📭 Memory collection does not exist yet - this is a new user');
      return [];
    }
    
    console.error('❌ Failed to get recent memories:', error);
    return [];
  }
}
/**
 * ============================================
 * COMPLETE LLM-POWERED SMART SAVEMEMORY
 * ============================================
 */

async saveMemory(memoryData) {
  if (!this._initialized) await this.initialize();
  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready');
    return null;
  }

  try {
    const memoryText = memoryData.memory || '';

    if (memoryText.trim().length < 5) {
      console.log('⏭️ Memory too short, skipping');
      return null;
    }

    console.log('🤖 LLM analyzing:', memoryText.substring(0, 60) + '...');

    const conversationMessages = memoryData.conversationMessages || [];
    const llmAnalysis = await this.llmAnalyzeSaveDecision(memoryText, conversationMessages);

    console.log('📊 LLM Analysis:', {
      shouldSave: llmAnalysis.shouldSave,
      needsClarification: llmAnalysis.needsClarification,
      clarificationQuestion: llmAnalysis.clarificationQuestion,
      reasoning: llmAnalysis.reasoning,
      cleanedMemory: llmAnalysis.cleanedMemory?.substring(0, 60)
    });

    // ============================================
    // CASE: Vague event - needs clarification
    // Return the question so the caller can ask user
    // ============================================
    if (llmAnalysis.needsClarification && llmAnalysis.clarificationQuestion) {
      console.log('❓ Vague event - needs clarification:', llmAnalysis.clarificationQuestion);
      return {
        needsClarification: true,
        clarificationQuestion: llmAnalysis.clarificationQuestion,
        pendingMemoryText: memoryText
      };
    }

    // ============================================
    // CASE: Not worth saving
    // ============================================
    if (!llmAnalysis.shouldSave) {
      console.log(`⏭️ LLM says skip: ${llmAnalysis.reasoning}`);
      return null;
    }

    // ============================================
    // Use cleanedMemory if LLM provided it
    // ============================================
    const finalMemoryText = (llmAnalysis.cleanedMemory && llmAnalysis.cleanedMemory.trim().length > 5)
      ? llmAnalysis.cleanedMemory.trim()
      : memoryText;

    console.log('📝 Final memory text to save:', finalMemoryText);

    // ============================================
    // Check if a similar memory already exists to UPDATE
    // instead of creating a duplicate
    // ============================================
    const existingMemory = await this.findSimilarMemory(finalMemoryText);

    if (existingMemory) {
      console.log('🔄 Similar memory found - updating instead of duplicating:', existingMemory.id);
      
      const memoryRef = doc(this.db, 'users', this.currentUser.uid, 'conversationMemory', existingMemory.id);
      const now = new Date();

      await updateDoc(memoryRef, {
        memory: finalMemoryText,
        updatedAt: serverTimestamp(),
        lastMentionedAt: Timestamp.fromDate(now),
        mentionCount: (existingMemory.mentionCount || 1) + 1,
        extensionHistory: arrayUnion({
          extendedAt: now.toISOString(),
          reason: 'clarification_update',
          previousMemory: existingMemory.memory,
          newMemory: finalMemoryText
        })
      });

      console.log('✅ Memory updated with clarified info');
      return { memoryId: existingMemory.id, updated: true, memory: finalMemoryText };
    }

    // ============================================
    // No similar memory - save fresh
    // ============================================
    const isDuplicate = await this.checkMemoryDuplicate(finalMemoryText);
    if (isDuplicate) {
      console.log('⏭️ Already saved this memory');
      return null;
    }

    const finalImportance = llmAnalysis.importance || 'medium';

    const timeframe = await this.classifyMemoryTimeframe({
      memory: finalMemoryText,
      type: llmAnalysis.type,
      importance: finalImportance
    });

    let expiresAt = null;
    let baseExpiryDate = new Date();
    let preservationReason = 'general';
    const now = new Date();

    const eventDate = this.extractEventDate(finalMemoryText);

    if (eventDate && eventDate > now) {
      baseExpiryDate = new Date(eventDate);
      baseExpiryDate.setDate(baseExpiryDate.getDate() + 1);
      preservationReason = 'event_deadline';
      expiresAt = Timestamp.fromDate(baseExpiryDate);
    } else if (timeframe === 'SHORT_TERM') {
      baseExpiryDate = new Date(now);
      baseExpiryDate.setDate(baseExpiryDate.getDate() + 30);
      preservationReason = 'short_term_default';
      expiresAt = Timestamp.fromDate(baseExpiryDate);
    } else {
      preservationReason = 'permanent_memory';
    }

    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');

    const docData = {
      preptuneUserId: this.preptuneUserId,
      userId: this.currentUser.uid,
      memory: finalMemoryText,
      type: llmAnalysis.type || 'general',
      category: llmAnalysis.category || 'general',
      topic: llmAnalysis.topic || 'general',
      importance: finalImportance,
      confidence: llmAnalysis.confidence || 0.7,
      tags: llmAnalysis.tags || [],
      timeframe: timeframe,
      expiresAt: expiresAt,
      isActive: true,
      baseExpiryAt: Timestamp.fromDate(baseExpiryDate),
      lastMentionedAt: Timestamp.fromDate(now),
      mentionCount: 1,
      isBeingDiscussed: true,
      relevanceScore: finalImportance === 'high' ? 0.95 : (finalImportance === 'medium' ? 0.80 : 0.60),
      preservationReason: preservationReason,
      shouldExtend: true,
      extensionHistory: [
        {
          extendedAt: now.toISOString(),
          reason: 'initial_save',
          newExpiryAt: baseExpiryDate.toISOString(),
          relevanceScore: finalImportance === 'high' ? 0.95 : 0.80,
          llmReasoning: llmAnalysis.reasoning
        }
      ],
      savedAt: Timestamp.fromDate(now),
      createdAt: serverTimestamp(),
      mentionedAt: Timestamp.fromDate(now),
      timestamp: now.toISOString(),
      dayOfWeek: now.toLocaleDateString('en-US', { weekday: 'long' }),
      timeOfDay: now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
      fullDate: now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    };

    const docRef = await addDoc(memoryRef, docData);

    console.log(`✅ Memory saved:`, {
      memoryId: docRef.id,
      memory: finalMemoryText.substring(0, 50),
      type: llmAnalysis.type,
      importance: finalImportance,
      timeframe: timeframe
    });

    return { memoryId: docRef.id, ...docData };

  } catch (error) {
    console.error('❌ Failed to save memory:', error.message);
    throw error;
  }
}
async findSimilarMemory(memoryText) {
  if (!this.isReady()) return null;

  try {
    // Pull recent active memories
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    const q = query(
      memoryRef,
      where('isActive', '==', true),
      where('type', '==', 'event'),
      orderBy('mentionedAt', 'desc'),
      limit(20)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const candidates = [];
    snapshot.forEach(doc => {
      candidates.push({ id: doc.id, ...doc.data() });
    });

    if (candidates.length === 0) return null;

    // ✅ Ask LLM if any existing memory is about the same thing
    const candidateList = candidates
      .map((m, idx) => `${idx + 1}. "${m.memory}"`)
      .join('\n');

    const response = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
          content: `You are checking if a new memory is about the same event as any existing memory.

NEW MEMORY: "${memoryText}"

EXISTING MEMORIES:
${candidateList}

If any existing memory is clearly about the same event or schedule, return its index.
If none match, return null.

Return ONLY valid JSON:
{
  "matchFound": true or false,
  "matchIndex": number or null,
  "reason": "brief explanation"
}`
        },
        { role: 'user', content: 'Find match' }
      ],
      {
        temperature: 0.2,
        max_tokens: 100
      }
    );

    const content = response.choices[0].message.content.trim();
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;

    const result = JSON.parse(jsonMatch[0]);

    if (result.matchFound && result.matchIndex != null) {
      const idx = result.matchIndex - 1;
      if (idx >= 0 && idx < candidates.length) {
        console.log(`✅ Similar memory found at index ${result.matchIndex}: "${candidates[idx].memory.substring(0, 40)}..."`);
        return candidates[idx];
      }
    }

    return null;

  } catch (error) {
    console.error('❌ findSimilarMemory error:', error);
    return null;
  }
}
/**
 * ============================================
 * NEW: LLM analyzes if worth saving + extracts details
 * ============================================
 */
async llmAnalyzeSaveDecision(userStatement, conversationContext = []) {
  try {
    console.log('🤖 LLM analyzing with context...');

    const recentContext = conversationContext
      .slice(-5)
      .map(m => `${m.type === 'user' ? 'User' : 'AI'}: ${m.content}`)
      .join('\n');

    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentDate = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const currentTime = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    const data = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
          content: `Analyze if this statement should be saved as a long-term memory about the user.

CURRENT DATE: ${currentDate}
CURRENT DAY: ${currentDay}
CURRENT TIME: ${currentTime}

CONVERSATION CONTEXT:
${recentContext}

═══════════════════════════════════════
✅ SAVE - reveals something meaningful:
═══════════════════════════════════════
- Identity: "I'm a nurse", "I'm 22", "I live in Miami"
- Preferences: "I love horror movies", "I hate seafood"
- Skills: "I speak Spanish", "I play guitar"
- Goals: "I want to lose weight", "I'm trying to get promoted"
- Relationships: "my sister is getting married", "my best friend is Alex"
- Health: "I have ADHD", "I'm vegan", "I'm allergic to peanuts"
- Achievements: "I just got promoted", "I finished my degree"
- Scheduled events WITH a clear specific date OR clear recurring pattern:
  ✅ "I have class every Tuesday and Thursday at 5pm" (clear recurrence)
  ✅ "I have a birthday party tomorrow Saturday at 6pm" (specific date)
  ✅ "I work every weekday from 9 to 5" (clear recurrence)
  ✅ "I have an exam on March 5th" (specific date)

═══════════════════════════════════════
❌ DO NOT SAVE - junk, filler, vague:
═══════════════════════════════════════
- Greetings: "hi", "hey", "hello", "good morning", "what's up"
- Acknowledgments: "ok", "okay", "got it", "sure", "thanks", "alright"
- Filler responses: "pretty good", "not bad", "fine", "I'm good", "doing well"
- Current activity with no future relevance:
  "just laying down", "just chilling", "watching tv rn", "hanging out lol", "laying down rn"
- Vague reactions: "oh ok", "lol", "haha", "nah", "yeah", "cool", "nice"
- Questions the user asked the AI: "what's on my calendar", "what time is it", "what did we talk about"
- Commands to AI: "add this to my calendar", "remind me", "create a task", "set an alarm"
- ANY scheduled event that has NO specific date AND NO clear recurring pattern:
  ❌ "I have class this evening" → which evening? every day? unknown → DO NOT SAVE
  ❌ "I have a meeting later" → when exactly? unknown → DO NOT SAVE
  ❌ "I have something going on tonight" → too vague → DO NOT SAVE
  ❌ "I have class from 5:20 to 8" → what days? every day? unknown → DO NOT SAVE

═══════════════════════════════════════
⚠️ SCHEDULE/EVENT SPECIAL RULES:
═══════════════════════════════════════
Events MUST have either:
1. A specific date (today is ${currentDate}, use this to resolve "tomorrow", "this Saturday" etc.)
2. OR a clear recurring pattern (every Monday, every weekday, every Tuesday and Thursday)

If the event has a specific time but no clear day/date/recurrence → DO NOT SAVE.
Saving vague schedule events causes the AI to wrongly think they happen every single day.

When saving a schedule event, the cleanedMemory MUST include:
- Exact days or recurrence pattern
- Exact time
- Whether it is one-time or recurring
- Resolved actual date if it's a one-time event

EXAMPLES OF GOOD cleanedMemory FOR EVENTS:
✅ "You have class every Tuesday and Thursday from 5:20pm to 8:00pm (recurring weekly)"
✅ "You have a birthday party on Saturday February 22nd 2026 at 6:00pm (one-time)"
✅ "You work every weekday Monday through Friday from 9:00am to 5:00pm (recurring)"
✅ "You have a dentist appointment on March 5th 2026 at 2:00pm (one-time)"

═══════════════════════════════════════
RETURN ONLY VALID JSON:
═══════════════════════════════════════
{
  "shouldSave": boolean,
  "type": "personalFact|preference|skill|goal|emotion|habit|event|relationship|achievement|health|general",
  "category": "identity|work|education|relationships|health|hobbies|goals|achievements|preferences|schedule|challenges|values",
  "topic": "string",
  "importance": "high|medium|low",
  "confidence": 0.0-1.0,
  "tags": ["tag1", "tag2"],
  "reasoning": "brief explanation of why saving or skipping",
  "cleanedMemory": "the properly worded memory text to save — for events MUST include full recurrence/date context"
}`
        },
        {
          role: 'user',
          content: `Recent conversation:\n${recentContext}\n\nUser's statement: "${userStatement}"\n\nShould I save this?`
        }
      ],
      {
        temperature: 0.2,
        max_tokens: 350,
        tools: undefined
      }
    );

    const content = data.choices[0].message.content.trim();
    const jsonMatch = content.match(/\{[\s\S]*\}/);

    if (!jsonMatch) {
      return {
        shouldSave: false,
        reasoning: 'parse_error',
        confidence: 0
      };
    }

    const result = JSON.parse(jsonMatch[0]);

    console.log('📊 LLM Save Decision:', {
      shouldSave: result.shouldSave,
      type: result.type,
      importance: result.importance,
      reasoning: result.reasoning,
      cleanedMemory: result.cleanedMemory?.substring(0, 60)
    });

    return result;

  } catch (error) {
    console.error('❌ LLM analysis error:', error);
    return {
      shouldSave: false,
      reasoning: 'llm_error',
      confidence: 0
    };
  }
}
async detectMemoryQuery(userMessage) {
  if (!userMessage || userMessage.trim().length < 3) {
    console.log('⏭️ Message too short for memory query detection');
    return { isMemoryQuery: false };
  }

  try {
    const msg = userMessage.toLowerCase().trim();

    // ✅ FAST PATH: Check for explicit memory keywords FIRST
    const memoryKeywords = [
      'remember', 'remind me', 'what did i', 'what did you',
      'we talked', 'we discussed', 'earlier', 'before',
      'last time', 'last conversation', 'previously',
      'do you recall', 'do you remember', 'tell me about',
      'what was', 'how was', 'what happened to'
    ];

    const hasMemoryKeyword = memoryKeywords.some(keyword => msg.includes(keyword));
    
    if (!hasMemoryKeyword) {
      console.log('⏭️ No memory keywords found');
      return { isMemoryQuery: false };
    }

    console.log('🔍 Memory keyword detected, using SecureAPI...');

    // ✅ Use SecureAPI (no config check needed)
    const data = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
          content: `Determine if user is asking about PAST conversations or memories.

✅ YES - Memory queries:
- "Remember when I said..."
- "What did we talk about?"
- "Remind me what happened"
- "Do you recall..."
- "Tell me about when..."

❌ NO - Not memory queries:
- "Yeah", "okay", "sure"
- Regular conversation
- Asking for new information

Respond with ONLY valid JSON:
{
  "isMemoryQuery": boolean,
  "confidence": 0.0-1.0
}`
        },
        { role: 'user', content: userMessage }
      ],
      {
        temperature: 0.1,
        max_tokens: 50,
        tools: undefined
      }
    );

    const content = data.choices[0]?.message?.content || '';
    
    // ✅ Extract JSON from response
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.log('⏭️ Could not parse LLM response');
      return { 
        isMemoryQuery: hasMemoryKeyword,
        confidence: 0.6 
      };
    }

    const detection = JSON.parse(jsonMatch[0]);
    
    // ✅ Require HIGH confidence
    const isQuery = detection.isMemoryQuery && detection.confidence > 0.7;
    
    console.log(`✅ Memory query detection:`, { isQuery, confidence: detection.confidence });
    
    return {
      isMemoryQuery: isQuery,
      confidence: detection.confidence
    };

  } catch (error) {
    console.error('❌ Memory query detection error:', error);
    return { isMemoryQuery: false };
  }
}

// ============================================
// FIX 2: NEW FUNCTION - Detect memory references
// ============================================
async detectAndUpdateMemoryReferences(userMessage, conversationMessages = [], cachedMemories = []) {
  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready');
    return [];
  }

  if (!userMessage || userMessage.trim().length < 5) {
    return [];
  }

  try {
    console.log('🔍 Scanning for memory references in user message...');

    const config = this.azureConfig;
    
    if (!config?.endpoint || !config?.apiKey) {
      console.warn('⚠️ No Azure config set - cannot detect memory references');
      return [];
    }

    // ✅ USE PASSED CACHED MEMORIES instead of fetching
    const recentMemories = cachedMemories.length > 0 
      ? cachedMemories 
      : await this.getShortTermMemories(50);
    
    if (recentMemories.length === 0) {
      console.log('📭 No short-term memories to check');
      return [];
    }

    // Rest of function stays the same...
    const memoryList = recentMemories.map((mem, idx) => 
      `${idx + 1}. "${mem.memory}" (last mentioned: ${this.getNaturalTimeAgo(mem.lastMentionedAt || mem.savedAt)})`
    ).join('\n');

    const response = await fetch(
      `${config.endpoint}/openai/deployments/${config.deployment}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': config.apiKey
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `Analyze if the user message references or discusses any of these previously saved memories.

SAVED MEMORIES:
${memoryList}

Check for:
✅ Direct references: "remember when I...", "that thing I mentioned...", "do you recall..."
✅ Topic continuations: "how's the project going?" (if project is in memories)
✅ Related discussions: talking about same topic but different angle
✅ Updates: "I finished it!", "didn't work out", "it's coming up!", "it's happening soon!"
✅ Follow-ups: "did you ask me about...?", "what did I say about..."
✅ Progress checks: "is that still happening?", "how did that go?"

Return ONLY valid JSON:
{
  "foundReferences": true or false,
  "referencedMemories": [
    {
      "index": 1,
      "memory": "exact memory text from list",
      "referenceType": "direct_mention|topic_continuation|update|follow_up|progress_check",
      "confidence": 0.0 to 1.0,
      "contextSnippet": "what the user said about it"
    }
  ],
  "userEngagementLevel": "high|medium|low",
  "shouldExtendMemories": true or false
}`
            },
            {
              role: 'user',
              content: `User message: "${userMessage}"\n\nRecent conversation context: ${conversationMessages.slice(-5).map(m => m.content).join(' | ').substring(0, 500)}`
            }
          ],
          temperature: 0.3,
          max_tokens: 400
        })
      }
    );

    if (response.ok) {
      const data = await response.json();
      const content = data.choices[0].message.content.trim();
      
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const detection = JSON.parse(jsonMatch[0]);
        
        if (detection.foundReferences && detection.referencedMemories.length > 0) {
          console.log(`✅ Found ${detection.referencedMemories.length} memory reference(s)`);
          
          // Update each referenced memory
          const updatedIds = [];
          for (const ref of detection.referencedMemories) {
            const memoryId = recentMemories[ref.index - 1]?.id;
            if (memoryId) {
              const success = await this.updateMemoryOnMention(
                memoryId,
                ref.referenceType,
                userMessage
              );
              if (success) {
                updatedIds.push(memoryId);
              }
            }
          }
          
          return updatedIds;
        } else {
          console.log('📭 No memory references detected');
        }
      }
    }

    return [];

  } catch (error) {
    console.error('❌ Memory reference detection failed:', error);
    return [];
  }
}
async getMemorySummaryForAI(userMessage, cachedMemories = []) {
  if (!this.isReady()) {
    return null;
  }

  try {
    // ✅ USE CACHED MEMORIES if provided
    const memoriesToUse = cachedMemories.length > 0 
      ? cachedMemories 
      : await this.getRecentMemories(20, 30);

    if (memoriesToUse.length === 0) {
      return null;
    }

    const memoryText = memoriesToUse
      .map(m => `- ${m.memory} (${this.getNaturalTimeAgo(m.lastMentionedAt || m.savedAt)})`)
      .join('\n');

    return `📝 RELEVANT MEMORIES:\n${memoryText}\n\nUse these memories to provide context-aware responses.`;

  } catch (error) {
    console.error('❌ Memory summary error:', error);
    return null;
  }
}
// ============================================
// FIX 3: NEW FUNCTION - Update memory on mention
// ============================================
// ADD this new function to ConversationMemoryService:

async updateMemoryOnMention(memoryId, referenceType, userMessage) {
  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready');
    return false;
  }

  try {
    const memoryRef = doc(
      this.db,
      'users',
      this.currentUser.uid,
      'conversationMemory',
      memoryId
    );

    // Get the memory document
    const memorySnap = await getDocs(
      query(
        collection(this.db, 'users', this.currentUser.uid, 'conversationMemory'),
        where('__name__', '==', memoryId)
      )
    );

    if (memorySnap.empty) {
      console.warn(`⚠️ Memory not found: ${memoryId}`);
      return false;
    }

    const memory = memorySnap.docs[0].data();
    const now = new Date();
    const currentMentionCount = (memory.mentionCount || 0) + 1;
    
    // Calculate extension based on reference type
    let relevanceBoost = 0;
    let extensionDays = 0;

    switch (referenceType) {
      case 'direct_mention':
        relevanceBoost = 0.15;
        extensionDays = 60;
        break;
      case 'update':
        relevanceBoost = 0.25;
        extensionDays = 90;
        break;
      case 'progress_check':
        relevanceBoost = 0.20;
        extensionDays = 75;
        break;
      case 'topic_continuation':
        relevanceBoost = 0.10;
        extensionDays = 45;
        break;
      case 'follow_up':
        relevanceBoost = 0.20;
        extensionDays = 75;
        break;
      default:
        extensionDays = 30;
        relevanceBoost = 0.05;
    }

    const newRelevanceScore = Math.min((memory.relevanceScore || 0.8) + relevanceBoost, 1.0);
    
    // Calculate new expiry
    let newExpiryDate = new Date(now);
    
    if (currentMentionCount >= 3) {
      // If mentioned 3+ times, extend significantly
      newExpiryDate.setDate(newExpiryDate.getDate() + Math.max(extensionDays, 90));
    } else {
      newExpiryDate.setDate(newExpiryDate.getDate() + extensionDays);
    }

    // Update memory document
    await updateDoc(memoryRef, {
      lastMentionedAt: Timestamp.fromDate(now),
      mentionCount: currentMentionCount,
      relevanceScore: newRelevanceScore,
      isBeingDiscussed: true,
      expiresAt: Timestamp.fromDate(newExpiryDate),
      shouldExtend: newRelevanceScore > 0.6,
      preservationReason: `actively_discussed_${currentMentionCount}_times`,
      extensionHistory: arrayUnion({
        extendedAt: now.toISOString(),
        reason: `mentioned_via_${referenceType}`,
        mentionCount: currentMentionCount,
        userMessageSnippet: userMessage.substring(0, 100),
        newExpiryAt: newExpiryDate.toISOString(),
        newRelevanceScore: newRelevanceScore,
        extensionDays: extensionDays
      })
    });

    console.log(`✅ Memory updated on mention:`, {
      memoryId: memoryId,
      referenceType: referenceType,
      newMentionCount: currentMentionCount,
      newRelevanceScore: (newRelevanceScore * 100).toFixed(0) + '%',
      newExpiryDate: newExpiryDate.toDateString(),
      extensionDays: extensionDays
    });

    return true;

  } catch (error) {
    console.error('❌ Failed to update memory on mention:', error);
    return false;
  }
}

// ============================================
// FIX 4: NEW FUNCTION - Smart cleanup
// ============================================
// ADD this new function to ConversationMemoryService:

async smartCleanupExpiredMemories() {
  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready');
    return { deleted: 0, extended: 0, preserved: 0 };
  }

  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const now = new Date();
    const q = query(
      memoryRef,
      where('timeframe', '==', 'SHORT_TERM'),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const batch = writeBatch(this.db);
    
    let deletedCount = 0;
    let extendedCount = 0;
    let preservedCount = 0;

    console.log(`🔍 Smart cleanup scanning ${querySnapshot.size} short-term memories...`);

    const docsArray = querySnapshot.docs;
    
    for (const docSnap of docsArray) {
      const memory = docSnap.data();
      const expiryDate = memory.expiresAt?.toDate();
      
      if (!expiryDate) continue;

      // Not yet expired
      if (now <= expiryDate) {
        preservedCount++;
        continue;
      }

      // ✅ SMART DECISION: Before deleting, check if still relevant
      const shouldDelete = await this.shouldDeleteMemory(memory, now);

      if (shouldDelete) {
        // Delete it
        batch.update(docSnap.ref, { 
          isActive: false,
          deletedAt: Timestamp.fromDate(now),
          deletionReason: 'irrelevant_no_recent_mentions'
        });
        deletedCount++;

        console.log(`🗑️ Deleted irrelevant memory: "${memory.memory.substring(0, 40)}..."`);

      } else {
        // Extend it - user is still engaging with this
        const newExpiryDate = new Date(now);
        newExpiryDate.setDate(newExpiryDate.getDate() + 60); // Add 2 months
        
        batch.update(docSnap.ref, {
          expiresAt: Timestamp.fromDate(newExpiryDate),
          relevanceScore: Math.min((memory.relevanceScore || 0.8) + 0.05, 1.0),
          preservationReason: 'preserved_due_to_engagement',
          extensionHistory: arrayUnion({
            extendedAt: now.toISOString(),
            reason: 'smart_cleanup_extension',
            newExpiryAt: newExpiryDate.toISOString(),
            preservedBecause: 'active_relevance_or_mention_history'
          })
        });
        extendedCount++;

        console.log(`⏱️ Extended memory (user still engaged): "${memory.memory.substring(0, 40)}..."`);
      }
    }

    if (deletedCount > 0 || extendedCount > 0) {
      await batch.commit();
    }

    console.log(`✅ Smart cleanup complete:`, {
      deleted: deletedCount,
      extended: extendedCount,
      preserved: preservedCount,
      total: querySnapshot.size
    });

    return { deleted: deletedCount, extended: extendedCount, preserved: preservedCount };

  } catch (error) {
    console.error('❌ Smart cleanup failed:', error);
    return { deleted: 0, extended: 0, preserved: 0 };
  }
}

// ============================================
// FIX 5: NEW FUNCTION - Determine if should delete
// ============================================
// ADD this new function to ConversationMemoryService:

async shouldDeleteMemory(memory, currentDate) {
  try {
    const daysSinceLastMention = memory.lastMentionedAt 
      ? this.getHoursAgo(memory.lastMentionedAt) / 24 
      : this.getHoursAgo(memory.savedAt) / 24;

    // ✅ RULE 1: If mentioned recently (< 7 days), KEEP IT
    if (daysSinceLastMention < 7) {
      console.log(`✅ Keep: Recently mentioned (${Math.floor(daysSinceLastMention)} days ago)`);
      return false;
    }

    // ✅ RULE 2: If HIGH importance, give more time (< 30 days)
    if (memory.importance === 'high' && daysSinceLastMention < 30) {
      console.log(`✅ Keep: High importance memory`);
      return false;
    }

    // ✅ RULE 3: If mentioned 3+ times, user clearly cares - KEEP
    if ((memory.mentionCount || 0) >= 3) {
      console.log(`✅ Keep: Mentioned ${memory.mentionCount} times by user`);
      return false;
    }

    // ✅ RULE 4: Relevance score > 0.7 = still relevant
    if ((memory.relevanceScore || 0) > 0.7) {
      console.log(`✅ Keep: High relevance score (${((memory.relevanceScore || 0) * 100).toFixed(0)}%)`);
      return false;
    }

    // ✅ RULE 5: If it has an event date in future, KEEP
    if (memory.baseExpiryAt) {
      const baseExpiry = memory.baseExpiryAt.toDate();
      if (currentDate < baseExpiry) {
        console.log(`✅ Keep: Event date still in future`);
        return false;
      }
    }

    // Otherwise, it's truly irrelevant
    console.log(`❌ Delete: No recent mentions, low relevance, irrelevant`);
    return true;

  } catch (error) {
    console.error('❌ shouldDeleteMemory error:', error);
    return false; // When in doubt, keep it
  }
}

/**
 * ============================================
 * SMART LLM-BASED MEMORY RECALL DETECTION
 * ============================================
 */
async detectConversationSummaryRequest(userMessage) {
  const msg = userMessage.toLowerCase().trim();
  const wordCount = msg.split(/\s+/).length;
  
  console.log('🔍 Analyzing message for recap request:', {
    message: msg,
    wordCount: wordCount
  });
  
  if (wordCount <= 2) {
    console.log('⏭️ Message too short for recap detection');
    return { isRequestingRecap: false, timeframe: undefined };
  }

  try {
    console.log('🤖 Using LLM to intelligently detect recall intent...');

    const data = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
          content: `You are a memory recall intent detector. Analyze if the user is asking about or trying to recall past conversations.

DETECT RECALL QUERIES:
✅ YES - User is asking to recall/remember/discuss past:
- "what did we talk about yesterday?"
- "do you remember when i mentioned xyz"
- "remind me what happened last week"
- "what did i say about that"
- "tell me about our conversation from friday"
- "what memories do you have about me"

❌ NO - User is NOT asking to recall:
- "hi how are you" (greeting)
- "thanks for that" (acknowledgment)
- "okay lets do that" (command)
- "i'm feeling good today" (sharing info)

DETECT TIMEFRAME if mentioned:
- Yesterday, today, this morning, tonight
- Last week, last month, last year
- N days ago, N weeks ago

Return ONLY valid JSON:
{
  "isRequestingRecap": boolean,
  "timeframe": "exact timeframe or null",
  "confidence": 0.0-1.0,
  "reason": "brief explanation"
}`
        },
        {
          role: 'user',
          content: userMessage
        }
      ],
      {
        temperature: 0.2,
        max_tokens: 200,
        tools: undefined
      }
    );

    const content = data.choices[0]?.message?.content?.trim() || '';
    
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.log('⏭️ Could not parse LLM response');
      return { 
        isRequestingRecap: false, 
        timeframe: undefined 
      };
    }

    const detection = JSON.parse(jsonMatch[0]);
    
    console.log('✅ LLM Detection Result:', {
      isRequestingRecap: detection.isRequestingRecap,
      confidence: detection.confidence,
      timeframe: detection.timeframe
    });

    if (!detection.isRequestingRecap || detection.confidence < 0.7) {
      return { 
        isRequestingRecap: false,
        timeframe: detection.timeframe,
        confidence: detection.confidence
      };
    }

    return {
      isRequestingRecap: detection.isRequestingRecap,
      timeframe: detection.timeframe,
      confidence: detection.confidence,
      reason: detection.reason
    };

  } catch (error) {
    console.error('❌ LLM detection error:', error);
    return { 
      isRequestingRecap: false, 
      timeframe: undefined 
    };
  }
}

/**
 * ============================================
 * ALSO ADD THIS NEW METHOD - for extracting timeframe from detected recall
 * This runs AFTER LLM confirms it's a recall query
 * ============================================
 */

extractTimeframeFromMessage(userMessage) {
  const msg = userMessage.toLowerCase();
  
  // TODAY
  if (/today|this morning|this afternoon|tonight|earlier today/i.test(msg)) {
    return 'today';
  }
  
  // YESTERDAY
  if (/yesterday/i.test(msg)) {
    return 'yesterday';
  }
  
  // THIS WEEK
  if (/this week/i.test(msg)) {
    return 'this week';
  }
  
  // LAST WEEK
  if (/last week/i.test(msg)) {
    return 'last week';
  }
  
  // N DAYS AGO
  const daysMatch = msg.match(/(\d+)\s*days?\s*ago/i);
  if (daysMatch) {
    return `${daysMatch[1]} days ago`;
  }
  
  // N WEEKS AGO
  const weeksMatch = msg.match(/(\d+)\s*weeks?\s*ago/i);
  if (weeksMatch) {
    return `${weeksMatch[1]} weeks ago`;
  }
  
  // N MONTHS AGO
  const monthsMatch = msg.match(/(\d+)\s*months?\s*ago/i);
  if (monthsMatch) {
    return `${monthsMatch[1]} months ago`;
  }
  
  // LAST MONTH
  if (/last month/i.test(msg)) {
    return 'last month';
  }
  
  // THIS MONTH
  if (/this month/i.test(msg)) {
    return 'this month';
  }
  
  // LAST YEAR
  if (/last year|year ago/i.test(msg)) {
    return 'last year';
  }
  
  return null;
}
async getRecentConversationSummaries(hoursBack = 24) {
  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready');
    return [];
  }

  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const cutoffTime = new Date();
    cutoffTime.setHours(cutoffTime.getHours() - hoursBack);
    
    console.log(`📝 Fetching conversation summaries from last ${hoursBack} hours`);
    
    const q = query(
      memoryRef,
      where('type', '==', 'conversation_summary'),
      where('savedAt', '>=', Timestamp.fromDate(cutoffTime)),
      orderBy('savedAt', 'desc'),
      limit(10)
    );
    
    const snapshot = await getDocs(q);
    const summaries = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      summaries.push({
        id: doc.id,
        ...data,
        hoursAgo: this.getHoursAgo(data.savedAt)
      });
    });

    console.log(`✅ Retrieved ${summaries.length} recent conversation summaries`);
    return summaries;

  } catch (error) {
    console.error('❌ Failed to get conversation summaries:', error);
    return [];
  }
}
/**
 * ✅ NEW: Get summaries from ACTUAL calendar date (not just hours back)
 * This correctly handles "yesterday" vs "today" vs "last week"
 */
async getConversationSummariesByActualDate(timeframe) {
  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready');
    return [];
  }

  try {
    const now = new Date();
    let startDate, endDate;

    // ============================================
    // Calculate ACTUAL date ranges
    // ============================================
    
    if (timeframe === 'yesterday') {
      // YESTERDAY: Start of yesterday to end of yesterday
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);
      
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
      
      console.log('📅 YESTERDAY date range:', {
        start: startDate.toLocaleString(),
        end: endDate.toLocaleString()
      });
    } 
    else if (timeframe === 'today') {
      // TODAY: Start of today to now
      startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      
      endDate = new Date(now);
      
      console.log('📅 TODAY date range:', {
        start: startDate.toLocaleString(),
        end: endDate.toLocaleString()
      });
    }
    else if (timeframe === 'last week') {
      // LAST WEEK: 7 days ago to yesterday
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      
      endDate = new Date(now);
      endDate.setDate(endDate.getDate() - 1);
      endDate.setHours(23, 59, 59, 999);
      
      console.log('📅 LAST WEEK date range:', {
        start: startDate.toLocaleString(),
        end: endDate.toLocaleString()
      });
    }
    else {
      // DEFAULT: Use hours-based search
      console.warn('⚠️ Unknown timeframe, using default search');
      return await this.getRecentConversationSummaries(72);
    }

    // ============================================
    // Query Firestore with EXACT date range
    // ============================================
    
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const q = query(
      memoryRef,
      where('type', '==', 'conversation_summary'),
      where('savedAt', '>=', Timestamp.fromDate(startDate)),
      where('savedAt', '<=', Timestamp.fromDate(endDate)),
      orderBy('savedAt', 'desc'),
      limit(20)
    );
    
    const snapshot = await getDocs(q);
    const summaries = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      const savedDate = data.savedAt.toDate();
      
      summaries.push({
        id: doc.id,
        ...data,
        hoursAgo: this.getHoursAgo(data.savedAt),
        actualDate: savedDate,
        dateString: savedDate.toLocaleDateString(),
        timeString: savedDate.toLocaleTimeString()
      });
    });

    console.log(`✅ Found ${summaries.length} summaries in ${timeframe}:`, {
      dateRange: `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
      summaries: summaries.map(s => ({
        date: s.dateString,
        time: s.timeString,
        preview: s.memory?.substring(0, 50)
      }))
    });

    return summaries;

  } catch (error) {
    console.error('❌ Failed to get summaries by date:', error);
    return [];
  }
}

  async searchMemories(searchTerm, limitCount = 5) {
    if (!this.isReady()) return [];

    try {
      const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
      
      const q = query(
        memoryRef,
        where('tags', 'array-contains', searchTerm.toLowerCase()),
        orderBy('mentionedAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      const memories = [];

      querySnapshot.forEach((doc) => {
        memories.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return memories;
    } catch (error) {
      console.error('Failed to search memories:', error);
      return [];
    }
  }

  extractKeywords(message) {
    const lowerMessage = message.toLowerCase();
    
    const stopwords = new Set([
      'a', 'an', 'the', 'is', 'was', 'are', 'were',
      'i', 'you', 'what', 'how', 'can', 'do'
    ]);
    
    const words = lowerMessage.match(/\b[a-z]{3,}\b/g) || [];
    return words.filter(word => !stopwords.has(word));
  }

  // ============================================
  // ✅ AI CONTEXT (WITH FALLBACK FIX)
  // ============================================
async getMemorySummaryForAI(currentMessage = '') {
  try {
    if (!this.isReady()) {
      console.warn('⚠️ Memory service not ready');
      return null;
    }

    console.log('🧠 Loading ALL memories for AI context...');
    
    // ✅ LOAD BOTH short-term and long-term
    const shortTermMemories = await this.getShortTermMemories(15);
    const longTermMemories = await this.getLongTermMemories(25);

    // Combine and deduplicate
    const allMemories = [...(shortTermMemories || []), ...(longTermMemories || [])];
    
    if (allMemories.length === 0) {
      console.log('📭 No memories available');
      return null;
    }

    console.log(`✅ Retrieved ${allMemories.length} total memories for AI context`);

    // ✅ Make memories time-aware and organized
    const timeAwareSummary = this.makeMemoriesTimeAware(allMemories);
    
    if (!timeAwareSummary || timeAwareSummary.length === 0) {
      console.log('📭 No summary generated');
      return null;
    }

    console.log('✅ Memory summary ready for AI');
    return timeAwareSummary;

  } catch (error) {
    console.error('❌ Failed to get memory summary:', error);
    return null;
  }
}

/**
 * Convert memory timestamps to relative, time-aware descriptions
 * Makes memories context-aware about whether events have passed
 */
makeMemoriesTimeAware(memories) {
  if (!memories || memories.length === 0) return '';

  const now = new Date();
  
  // ============================================
  // FIX: Add logging to verify time calculations
  // ============================================
  console.log('📅 makeMemoriesTimeAware processing:');
  console.log('   Current time:', now.toLocaleString());
  console.log('   Number of memories:', memories.length);
  
  const timeAwareMemories = memories.map(memory => {
    let memoryText = memory.memory;
    
    // Smart conversion based on time elapsed
    const hoursAgo = this.getHoursAgo(memory.mentionedAt);
    
    // ============================================
    // FIX: Add logging for each memory
    // ============================================
    console.log(`   - "${memoryText.substring(0, 40)}..." (${hoursAgo} hours ago)`);
    
    // If something was "tomorrow" and 18+ hours have passed, update it
    if (hoursAgo >= 18 && memoryText.toLowerCase().includes('tomorrow')) {
      if (hoursAgo >= 36) {
        memoryText = memoryText.replace(/tomorrow/gi, 'yesterday');
      } else if (hoursAgo >= 24) {
        memoryText = memoryText.replace(/tomorrow/gi, 'today');
      } else {
        memoryText = memoryText.replace(/tomorrow/gi, 'happening very soon/today');
      }
    }
    
    // If something was "today" and 24+ hours have passed
    if (hoursAgo >= 24 && memoryText.toLowerCase().includes('today')) {
      memoryText = memoryText.replace(/today/gi, 'yesterday');
    }
    
    return {
      ...memory,
      memory: memoryText,
      hoursAgo,
      status: this.detectMemoryStatus(memoryText, hoursAgo)
    };
  });

  return this.buildTimeAwareMemorySummary(timeAwareMemories);
}


detectMemoryStatus(memoryText, hoursAgo) {
  if (hoursAgo === null) return 'unknown';
  
  const lowerText = memoryText.toLowerCase();
  
  // Upcoming events
  if (lowerText.includes('tomorrow') || lowerText.includes('next')) {
    return 'upcoming';
  }
  
  // Happening now or just happened (18-36 hours ago)
  if (hoursAgo >= 18 && hoursAgo <= 36) {
    return 'happening_or_recent';
  }
  
  // Just completed (past tense, 24-48 hours ago)
  if (hoursAgo >= 24 && lowerText.includes('yesterday')) {
    return 'completed';
  }
  
  // Recent but not time-specific
  if (hoursAgo <= 24) {
    return 'recent';
  }
  
  // Older memories
  if (hoursAgo > 168) { // >7 days
    return 'old';
  }
  
  return 'active';
}

/**
 * ✅ IMPROVED: Build time-aware memory summary for AI context
 * Organized by urgency with natural examples and clear instructions
 */
buildTimeAwareMemorySummary(memories) {
  if (!memories || memories.length === 0) return '';
  
  let summary = '\n\n🧠 MEMORIES - WHAT I KNOW ABOUT YOU:\n';
  
  // Group by status (urgency level)
  const byStatus = {};
  memories.forEach(mem => {
    if (!byStatus[mem.status]) {
      byStatus[mem.status] = [];
    }
    byStatus[mem.status].push(mem);
  });
  
  // ============================================
  // ⚡ JUST HAPPENED / HAPPENING NOW
  // ============================================
  if (byStatus['happening_or_recent'] && byStatus['happening_or_recent'].length > 0) {
    summary += '\n⚡ JUST HAPPENED / HAPPENING NOW (ASK ABOUT THIS!):\n';
    byStatus['happening_or_recent'].forEach(mem => {
      const timeRef = this.getNaturalTimeAgo(mem.mentionedAt);
      summary += `  • ${mem.memory} (${timeRef})\n`;
    });
    summary += `
WHAT TO DO:
- This is fresh - user probably wants to debrief
- Ask naturally about how it went or is going
- Sound curious and genuinely interested

EXAMPLES:
✅ "How'd that go?"
✅ "Wait, how did it turn out?"
✅ "So what happened?"
✅ "How are you feeling about how that went?"
✅ "Did it go the way you hoped?"

DON'T SAY:
❌ "Tell me more about that event from 2 hours ago"
❌ "I remember you mentioned that recently"
❌ "According to my records, you just..."
`;
  }
  
  // ============================================
  // 📅 UPCOMING / SOON
  // ============================================
  if (byStatus['upcoming'] && byStatus['upcoming'].length > 0) {
    summary += '\n📅 COMING UP SOON (CHECK IN ON READINESS):\n';
    byStatus['upcoming'].forEach(mem => {
      summary += `  • ${mem.memory}\n`;
    });
    summary += `
WHAT TO DO:
- They're thinking about something that's coming
- Ask how they're feeling about it
- Show you care about their readiness

EXAMPLES:
✅ "You ready for that?"
✅ "How are you feeling about it?"
✅ "Nervous or excited?"
✅ "Need any help prepping for it?"
✅ "That coming up soon, right? How you doing with it?"

DON'T SAY:
❌ "You previously mentioned an upcoming event"
❌ "I have a record of you saying..."
❌ "You indicated you were going to..."
`;
  }
  
  // ============================================
  // 📌 RECENT (LAST FEW DAYS)
  // ============================================
  if (byStatus['recent'] && byStatus['recent'].length > 0) {
    summary += '\n📌 RECENT - GOOD TO REFERENCE:\n';
    byStatus['recent'].forEach(mem => {
      const timeRef = this.getNaturalTimeAgo(mem.mentionedAt);
      summary += `  • ${mem.memory} (${timeRef})\n`;
    });
    summary += `
WHAT TO DO:
- Bring these up IF it fits naturally in conversation
- Don't force it - wait for natural connections
- These are fresh enough to reference casually

EXAMPLES:
✅ "Oh right, you mentioned that the other day"
✅ "How's that [thing] going?"
✅ "Any updates on [topic]?"
✅ "Wait, didn't you say something about that?"
✅ "You were telling me about [thing], how's it going?"

DON'T SAY:
❌ "Earlier you told me..."
❌ "You said 18 hours ago..."
❌ "In our last conversation..."
`;
  }
  
  // ============================================
  // 🔄 ONGOING / ACTIVE
  // ============================================
  if (byStatus['active'] && byStatus['active'].length > 0) {
    summary += '\n🔄 THINGS THEY\'RE WORKING ON:\n';
    byStatus['active'].forEach(mem => {
      summary += `  • ${mem.memory}\n`;
    });
    summary += `
WHAT TO DO:
- These are continuous - check in on progress
- Treat like personal projects they care about
- Show interest in their progress

EXAMPLES:
✅ "How's that going?"
✅ "Any progress on [goal]?"
✅ "Still working on that?"
✅ "What's the latest with [project]?"
✅ "Getting closer to finishing?"

DON'T SAY:
❌ "According to my notes on your ongoing..."
❌ "In my memory you're working on..."
❌ "You mentioned you're currently engaged with..."
`;
  }
  
  // ============================================
  // 👤 CORE IDENTITY (WHO THEY ARE)
  // ============================================
  if (byStatus['identity'] && byStatus['identity'].length > 0) {
    summary += '\n👤 WHO THEY ARE - ALWAYS RELEVANT:\n';
    byStatus['identity'].forEach(mem => {
      summary += `  • ${mem.memory}\n`;
    });
    summary += `
WHAT TO DO:
- Use this when giving advice or context
- Reference naturally when it connects to what they're saying
- This shapes how you understand them

EXAMPLES:
✅ "As a [job], you probably deal with that a lot"
✅ "Given that you [skill], you'd probably be good at..."
✅ "Makes sense - you've always been [trait]"
✅ "With your background in [field], what do you think?"
✅ "That's interesting coming from someone who [identity]"

DON'T SAY:
❌ "I have in my memory that you are..."
❌ "Based on your previous statements, you're..."
❌ "You told me you were..."
`;
  }
  
  // ============================================
  // ✅ COMPLETED / PAST
  // ============================================
  if (byStatus['completed'] && byStatus['completed'].length > 0) {
    summary += '\n✅ COMPLETED - ONLY MENTION IF THEY BRING IT UP:\n';
    byStatus['completed'].forEach(mem => {
      summary += `  • ${mem.memory}\n`;
    });
    summary += `
WHAT TO DO:
- These are done - don't bring them up unprompted
- Only reference if user mentions them
- Can use for context ("you mentioned you finished...")

EXAMPLES (only if relevant):
✅ "Oh yeah, how'd that exam go?"
✅ "You finished that project, right?"
✅ "You went through all that and came out fine"

DON'T USE FOR:
❌ Don't randomly ask about completed things
❌ Don't list all their past accomplishments
`;
  }
  
  // ============================================
  // 📦 OLD (7+ DAYS AGO)
  // ============================================
  if (byStatus['old'] && byStatus['old'].length > 0) {
    summary += '\n📦 OLDER MEMORIES (7+ days, rarely bring up):\n';
    byStatus['old'].forEach(mem => {
      summary += `  • ${mem.memory}\n`;
    });
    summary += `
WHAT TO DO:
- These are archived context
- Only use if directly relevant to current conversation
- Don't reference unless user mentions it

EXAMPLES (very rare):
✅ "Remember way back when you said you wanted to..."
✅ "That was a while ago, but you mentioned..."
`;
  }
  
  // ============================================
  // 🎯 CRITICAL MEMORY RULES
  // ============================================
  summary += `

═══════════════════════════════════════════════════════════════
🎯 MEMORY USAGE PRIORITY
═══════════════════════════════════════════════════════════════

1. ⚡ JUST HAPPENED
   Priority: HIGHEST
   Action: ALWAYS ask how it went (naturally)
   Tone: Curious, interested in their experience

2. 📅 UPCOMING
   Priority: HIGH
   Action: Ask how they're feeling/ready
   Tone: Supportive, checking in

3. 👤 IDENTITY
   Priority: HIGH
   Action: Use when giving advice
   Tone: Natural, contextual

4. 📌 RECENT
   Priority: MEDIUM
   Action: Bring up if conversation naturally goes there
   Tone: Casual, follow-up conversation

5. 🔄 ONGOING
   Priority: MEDIUM
   Action: Check in on progress when relevant
   Tone: Interested, supportive

6. ✅ COMPLETED
   Priority: LOW
   Action: Only if they mention it
   Tone: Acknowledging, supportive

7. 📦 OLD
   Priority: VERY LOW
   Action: Rarely mention unless directly relevant
   Tone: Only if natural

═══════════════════════════════════════════════════════════════
🗣️ HOW TO SOUND NATURAL (NOT ROBOTIC)
═══════════════════════════════════════════════════════════════

✅ NATURAL TIME TALK:
- "yesterday evening"
- "this morning"
- "last Friday"
- "a couple weeks ago"
- "not long ago"
- "while back"

❌ ROBOTIC TIME TALK:
- "19 hours ago on Wednesday at 12:22 AM"
- "Exactly 3.5 days ago"
- "On the timestamp of..."
- "According to our logs at 15:30..."

✅ NATURAL REFERENCES:
- "You mentioned wanting to..."
- "Didn't you say something about...?"
- "So about [topic] you were talking about..."
- "That thing you told me about"
- "Last time we talked about [topic]..."
- "You were saying..."

❌ ROBOTIC REFERENCES:
- "I remember you said..."
- "According to my records..."
- "In your previous message..."
- "You told me..."
- "I have documented that..."
- "I've been informed that..."
- "Per your statement..."

✅ NATURAL FOLLOW-UPS:
- "How'd that go?" (for recent events)
- "You ready for that?" (for upcoming)
- "How's that coming along?" (for ongoing)
- "What's the update?" (for progress)
- "Any luck with that?" (for goals)
- "So about [topic]..." (casual reference)

❌ ROBOTIC FOLLOW-UPS:
- "Tell me more about the event you mentioned"
- "Please provide an update on your previous project"
- "Elaborate on what you said earlier"
- "Can you give me details about..."

═══════════════════════════════════════════════════════════════
⚠️ CRITICAL: BE GENUINE, NOT PERFECT
═══════════════════════════════════════════════════════════════

Remember: You're a friend who happens to remember things, not a database.
- Don't try to reference EVERY memory
- Don't list multiple memories at once
- Don't bring up unrelated memories
- DO let the conversation flow naturally
- DO reference memories when they naturally fit
- DO sound like you're just remembering, not reading notes

If you're unsure whether to mention a memory: DON'T.
It's better to miss one reference than to force an awkward memory mention.
`;

  return summary;
}

 /**
   * ✅ LLM-POWERED: CLASSIFY MEMORY INTO SHORT_TERM OR LONG_TERM
   */
async classifyMemoryTimeframe(memoryData, azureConfig = null) {
  try {
    const memoryText = memoryData.memory || '';
    const importance = memoryData.importance || 'medium';

    if (!azureConfig || !azureConfig.endpoint || !azureConfig.apiKey) {
      console.warn('⚠️ No Azure config - cannot classify intelligently');
      return 'LONG_TERM';
    }

    console.log('🤔 LLM analyzing memory with enhanced decision tree...');
    
    const data = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
              content: `You are an EXPERT memory classification AI. Classify if a memory should be SHORT_TERM (6 months, auto-delete) or LONG_TERM (permanent).

═══════════════════════════════════════════════════════════════════════════════════════
🧠 DECISION TREE - FOLLOW EACH STEP IN ORDER
═══════════════════════════════════════════════════════════════════════════════════════

STEP 1: DOES IT HAVE A SPECIFIC DATE OR DEADLINE?
═══════════════════════════════════════════════════════════════════════════════════════
→ Look for dates, times, timeframes, deadlines, scheduled events

✅ SHORT_TERM Examples:
• "You have an exam on December 15th" (specific date)
• "Your soccer game is Friday at 3 PM" (specific date & time)
• "You're flying to Japan next week" (specific timeframe)
• "Interview scheduled for January 10th at 2 PM" (specific date)
• "Birthday party this Saturday evening" (specific date)
• "Project deadline: November 30th" (specific deadline)
• "Moving to a new apartment on January 5th" (specific date)
• "Wedding ceremony on June 15th at 4 PM" (specific date)
• "Dentist appointment Tuesday morning" (specific date)
• "Summer internship starts June 1st" (specific date)
→ IF YES TO ANY: CLASSIFY AS SHORT_TERM ✅

STEP 2: IS IT AN UPCOMING EVENT/PLAN (no specific date but clearly time-bound)?
═══════════════════════════════════════════════════════════════════════════════════════
→ Look for: "soon", "coming up", "going to", "planning to", "will be", "next semester"

✅ SHORT_TERM Examples:
• "You're planning to learn JavaScript soon" (no exact date but time-bound)
• "Going to start a new job next month" (upcoming but not specific)
• "Thinking about traveling in the spring" (upcoming season)
• "You're going to apply to graduate schools soon" (upcoming action)
• "Planning a weekend trip to the mountains" (upcoming event)
• "You want to start a fitness routine next week" (upcoming goal)
• "Going to redecorate your apartment" (upcoming project)
• "You're planning to take a course this semester" (upcoming)
• "Considering adopting a dog soon" (upcoming major life event)
• "You're going to visit family for the holidays" (upcoming)
→ IF YES TO ANY: CLASSIFY AS SHORT_TERM ✅

STEP 3: IS IT A TEMPORARY EMOTIONAL STATE OR SITUATIONAL CHALLENGE?
═══════════════════════════════════════════════════════════════════════════════════════
→ Look for: stressed about, worried about, anxious about, dealing with, struggling with, going through
→ Key: It's TIED TO A SPECIFIC SITUATION that will resolve

✅ SHORT_TERM Examples:
• "You're stressed about the upcoming exam" (tied to specific exam)
• "Feeling anxious about your job interview next week" (tied to specific event)
• "You're worried about your presentation tomorrow" (tied to specific event)
• "Struggling with homesickness while studying abroad" (tied to specific situation)
• "You're dealing with a difficult coworker situation" (situational)
• "Feeling overwhelmed by college coursework this semester" (temporary situation)
• "You're nervous about meeting your girlfriend's parents" (tied to specific event)
• "Stressed about finances until you get your next paycheck" (temporary state)
• "You're anxious about your medical test results" (tied to specific event)
• "Worried about failing your driving test" (tied to specific event)
→ IF YES TO ANY: CLASSIFY AS SHORT_TERM ✅

STEP 4: IS IT WHO THEY ARE (identity, core traits, deep preferences)?
═══════════════════════════════════════════════════════════════════════════════════════
→ Look for: statements about PERSONALITY, IDENTITY, PERMANENT CHARACTERISTICS
→ Key: Will still be true 5+ years from now

✅ LONG_TERM Examples (IDENTITY):
• "You're introverted and prefer quiet environments" (core personality trait)
• "You're studying mechanical engineering" (field of study - identity)
• "You're a software developer" (profession/career identity)
• "You're passionate about environmental sustainability" (core value)
• "You're a morning person" (core personality trait)
• "You're naturally creative and artistic" (core strength)
• "You have ADHD" (permanent diagnosis/condition)
• "You're left-handed" (permanent physical characteristic)
• "You're from Brazil originally" (permanent origin/background)
• "You have a dog named Max" (ongoing permanent relationship)

→ IF YES TO ANY: CLASSIFY AS LONG_TERM ✅

STEP 5: IS IT A PERMANENT PREFERENCE OR INTEREST?
═══════════════════════════════════════════════════════════════════════════════════════
→ Look for: likes, loves, dislikes, hates, enjoys, prefers, interested in
→ Key: They mention this multiple times OR it's clearly a lasting interest

✅ LONG_TERM Examples (PREFERENCES):
• "You love horror movies and thriller shows" (long-term preference)
• "You're a huge Manchester United fan" (ongoing fandom)
• "You hate seafood" (long-term dietary preference)
• "You love Italian food and cook it regularly" (ongoing interest)
• "You're interested in philosophy and metaphysics" (long-term interest)
• "You play guitar and want to get better" (ongoing hobby)
• "You love hiking and outdoor activities" (long-term interest)
• "You hate crowded places and prefer solitude" (permanent preference)
• "You're a bookworm and love reading sci-fi" (long-term interest)
• "You're obsessed with true crime podcasts" (long-term interest)

→ IF YES TO ANY: CLASSIFY AS LONG_TERM ✅

STEP 6: IS IT A SKILL, EXPERTISE, OR ABILITY THEY HAVE?
═══════════════════════════════════════════════════════════════════════════════════════
→ Look for: good at, skilled in, fluent in, knows how to, experienced with
→ Key: These don't expire or change quickly

✅ LONG_TERM Examples (SKILLS):
• "You're fluent in Spanish and French" (permanent skill)
• "You're an excellent public speaker" (long-term ability)
• "You know Python, JavaScript, and React" (technical skills)
• "You're a talented photographer" (long-term skill)
• "You're good at problem-solving and critical thinking" (ability)
• "You play the piano at an advanced level" (skill)
• "You're skilled at graphic design" (expertise)
• "You have 5 years of experience as a project manager" (skill/expertise)
• "You're certified as a yoga instructor" (skill/credential)
• "You have a black belt in Taekwondo" (skill/achievement)

→ IF YES TO ANY: CLASSIFY AS LONG_TERM ✅

STEP 7: IS IT AN ONGOING HABIT OR ROUTINE?
═══════════════════════════════════════════════════════════════════════════════════════
→ Look for: usually, always, often, regularly, every day, habit, routine
→ Key: These are PERMANENT PATTERNS in their behavior

✅ LONG_TERM Examples (HABITS/ROUTINES):
• "You usually wake up at 6 AM and go for a run" (routine)
• "You always have coffee before starting work" (daily habit)
• "You regularly meditate for 20 minutes each morning" (ongoing practice)
• "You're a night owl - you usually work late" (permanent pattern)
• "You always meal prep on Sundays for the week" (weekly routine)
• "You have a strict exercise routine 5 days a week" (ongoing habit)
• "You regularly volunteer at the animal shelter" (ongoing commitment)
• "You always read before bed as a way to unwind" (daily habit)
• "You're a morning journaler - you write every day" (daily practice)
• "You usually go to the gym 3-4 times a week" (ongoing routine)

→ IF YES TO ANY: CLASSIFY AS LONG_TERM ✅

STEP 8: IS IT A SERIOUS LIFE GOAL OR DREAM?
═══════════════════════════════════════════════════════════════════════════════════════
→ Look for: aspirations, dreams, life goals, career goals, long-term plans
→ Key: Big goals that take months/years to achieve or are ongoing

⚠️ THIS IS TRICKY - CONTEXT MATTERS:

✅ LONG_TERM GOALS (Keep these):
• "You want to become a doctor" (multi-year goal, career identity)
• "Your dream is to write a novel" (long-term aspiration)
• "You're working towards getting your PhD" (multi-year commitment)
• "You want to build a successful startup" (long-term goal)
• "Your goal is to become fluent in Mandarin" (long-term goal)
• "You want to travel to all 50 states" (long-term bucket list)
• "You're saving to buy a house" (long-term financial goal)
• "You want to be a professional musician" (career goal)
• "Your dream is to run a marathon" (long-term fitness goal)
• "You want to learn to code and transition careers" (long-term goal)

✅ SHORT_TERM GOALS (Don't keep these):
• "You want to finish your project by Friday" (specific short deadline)
• "You're trying to lose 5 pounds before the wedding" (tied to specific event)
• "You want to complete this online course by next month" (specific timeframe)
• "You're going to learn Python before your internship starts" (tied to deadline)
• "You want to memorize Spanish vocabulary for your exam" (exam-specific)
• "You're working on a presentation due next week" (specific deadline)
• "You want to read 3 books this month" (monthly goal)
• "You're planning to call your parents this weekend" (short-term plan)
• "You want to organize your desk before your friend visits" (event-tied)
• "You're going to practice guitar daily until the recital" (event-tied goal)

→ IF IT'S A SERIOUS LONG-TERM LIFE GOAL: LONG_TERM ✅
→ IF IT'S TIED TO A SPECIFIC DEADLINE/EVENT: SHORT_TERM ✅

STEP 9: IS IT INFORMATION ABOUT FAMILY/RELATIONSHIPS/LOVED ONES?
═══════════════════════════════════════════════════════════════════════════════════════
→ Look for: names, relationships, family connections, significant others

✅ LONG_TERM Examples (RELATIONSHIPS):
• "Your mom is a teacher and your dad is a doctor" (permanent family info)
• "You have two siblings - Sarah and Michael" (permanent family structure)
• "Your best friend is Alex, you've known them since 3rd grade" (relationship)
• "You're married to Jordan, been together for 7 years" (relationship status)
• "You have a 5-year-old daughter named Emma" (family relationship)
• "Your ex-boyfriend's name was Chris, dated for 2 years" (relationship history)
• "You have a close relationship with your grandmother" (family bond)
• "Your best friend just got engaged" (important life event about person)
• "You have a complicated relationship with your father" (ongoing dynamic)
• "Your partner is also a software engineer" (relationship detail)

→ IF YES TO ANY: CLASSIFY AS LONG_TERM ✅

STEP 10: DOES IT REFERENCE A PAST EVENT THAT'S ALREADY HAPPENED?
═══════════════════════════════════════════════════════════════════════════════════════
→ Look for: past tense, "yesterday", "last week", "last month"
→ Key: If event already passed, it's informational context

⚠️ CONTEXT MATTERS:

✅ LONG_TERM (Keep - provides ongoing context):
• "You graduated from NYU in 2022 with a degree in Computer Science" (background)
• "You worked at Google for 3 years" (work history/experience)
• "You've been living in New York for 5 years" (location context)
• "You broke your arm last year but it's fully healed now" (past medical event)

✅ SHORT_TERM (Delete - temporary situation resolved):
• "You had a bad day yesterday at work" (emotion already passed)
• "You were sick last week but feeling better now" (temporary condition)
• "You had an argument with your roommate yesterday" (temporary conflict)
• "You went to a party last Saturday and had fun" (past event, not relevant)

→ IF IT'S BACKGROUND/IDENTITY CONTEXT: LONG_TERM ✅
→ IF IT'S A RESOLVED TEMPORARY SITUATION: SHORT_TERM ✅

═══════════════════════════════════════════════════════════════════════════════════════
🚫 CRITICAL MISTAKES TO AVOID
═══════════════════════════════════════════════════════════════════════════════════════

❌ MISTAKE 1: Confusing "wants to learn X soon" with "wants to become X"
✅ CORRECT:
- "wants to learn Python before their internship starts" → SHORT_TERM
- "wants to become a software engineer" → LONG_TERM

❌ MISTAKE 2: Keeping temporary emotional states forever
✅ CORRECT:
- "stressed about the presentation tomorrow" → SHORT_TERM (tied to event)
- "you're naturally anxious person" → LONG_TERM (personality trait)

❌ MISTAKE 3: Deleting important identity information because it mentions time
✅ CORRECT:
- "you're currently studying engineering at MIT" → LONG_TERM (identity)
- "you're currently stressed about midterms" → SHORT_TERM (temporary state)

❌ MISTAKE 4: Keeping vague temporary plans
✅ CORRECT:
- "you're planning to watch a movie tonight" → SHORT_TERM
- "you love watching movies" → LONG_TERM

═══════════════════════════════════════════════════════════════════════════════════════
✅ RESPONSE FORMAT - MUST BE VALID JSON
═══════════════════════════════════════════════════════════════════════════════════════

{
  "classification": "SHORT_TERM" or "LONG_TERM",
  "reasoning": "Clear explanation of which step in decision tree you followed",
  "confidence": 0.0 to 1.0,
  "step_reached": "Which numbered step led to this decision",
  "examples_matched": ["What specific examples from above matched this memory"],
  "will_expire": true or false
}

Example responses:

{
  "classification": "SHORT_TERM",
  "reasoning": "Memory has specific deadline (December 15th exam). Matches Step 1 - specific date.",
  "confidence": 0.98,
  "step_reached": "Step 1: Specific Date/Deadline",
  "examples_matched": ["exam on December 15th"],
  "will_expire": true
}

{
  "classification": "LONG_TERM",
  "reasoning": "Memory about core identity (studying engineering). Matches Step 4 - permanent identity trait.",
  "confidence": 0.95,
  "step_reached": "Step 4: Identity/Who They Are",
  "examples_matched": ["studying mechanical engineering"],
  "will_expire": false
}

{
  "classification": "SHORT_TERM",
  "reasoning": "Temporary emotional state tied to specific event (job interview). Matches Step 3 - situational stress.",
  "confidence": 0.92,
  "step_reached": "Step 3: Temporary Emotional State",
  "examples_matched": ["anxious about job interview next week"],
  "will_expire": true
}

NOW CLASSIFY THIS MEMORY:
Memory: "${memoryText}"
Importance Level: ${importance}

Think through EACH STEP carefully. Reference specific examples from above.`
            },
            {
              role: 'user',
              content: `Classify this memory following the complete decision tree:\n\n"${memoryText}"\n\nImportance: ${importance}`
        }
      ],
      {
        temperature: 0.2,
        max_tokens: 500
      }
    );

    const content = data.choices[0].message.content.trim();
    
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const result = JSON.parse(jsonMatch[0]);
      
      console.log('🧠 LLM CLASSIFICATION RESULT:');
      console.log(`   Classification: ${result.classification}`);
      console.log(`   Confidence: ${(result.confidence * 100).toFixed(0)}%`);
      
      return result.classification;
    }

    console.warn('⚠️ LLM classification failed, defaulting to LONG_TERM');
    return 'LONG_TERM';

  } catch (error) {
    console.error('❌ Classification error:', error.message);
    return 'LONG_TERM';
  }
}
// ============================================
// FIXED: shouldSaveMemory - with Azure config fallback
// ============================================
async shouldSaveMemory(memoryText, azureConfig = null) {
  try {
    if (!memoryText || memoryText.trim().length < 5) {
      return false;
    }

    const finalConfig = azureConfig || this.azureConfig || window.azureConfig;

    if (!finalConfig || !finalConfig.endpoint || !finalConfig.apiKey) {
      console.warn('⚠️ No Azure config - allowing save by default');
      return memoryText.length > 10;
    }

    console.log('🤔 LLM deciding if worth remembering...');
    
    const data = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
              content: `Decide if this statement reveals something MEANINGFUL about who the person is.

✅ SAVE if it reveals:
1. **Identity**: "I'm a student", "I work as a teacher", "I'm 25 years old"
2. **Preferences**: "I love horror movies", "I hate seafood", "I'm a Yankees fan"
3. **Skills**: "I'm good at coding", "I speak Spanish", "I play guitar"
4. **Goals**: "I want to learn Python", "I'm trying to get fit"
5. **Beliefs/Values**: "I believe in X", "Family is important to me"
6. **Upcoming Events**: "I have an exam tomorrow", "Soccer game Friday"
7. **Ongoing Situations**: "I'm stressed about work", "I'm dealing with X"

❌ DON'T SAVE:
1. **Commands**: "Create a podcast", "Make flashcards", "Help me with X"
2. **Questions**: "What's 2+2?", "How do I learn Python?"
3. **Acknowledgments**: "OK", "Thanks", "Got it", "Yeah"
4. **Vague statements**: "We talked about stuff", "That's interesting"
5. **Casual filler**: "Hmm", "Uh", "Well...", "I see"
6. **Temporary actions**: "I'm currently watching X" (unless they say they LOVE it)

🤔 ASK YOURSELF:
"If I met this person 6 months from now, would this help me remember who they are?"

**RESPOND IN THIS EXACT JSON FORMAT:**
{
  "should_save": true or false,
  "reasoning": "Brief explanation of why",
  "category": "identity|preference|skill|goal|event|casual|command"
}`
            },
            {
              role: 'user',
         content: `Should I save this?\n\n"${memoryText}"`
        }
      ],
      {
        temperature: 0.1,
        max_tokens: 150
      }
    );

    const content = data.choices[0].message.content.trim();
    
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const result = JSON.parse(jsonMatch[0]);
      
      const emoji = result.should_save ? '✅ SAVE' : '⏭️ SKIP';
      console.log(`${emoji}: "${memoryText.substring(0, 50)}..."`);
      console.log(`   Category: ${result.category}`);
      console.log(`   Reasoning: ${result.reasoning}`);
      
      return result.should_save;
    }

    return false;

  } catch (error) {
    console.error('❌ Save decision error:', error.message);
    return false;
  }
}
  /**
   * ✅ LLM-POWERED: DETERMINE MEMORY IMPORTANCE
   */
async determineMemoryImportance(memoryText, azureConfig = null) {
  try {
    if (!memoryText || memoryText.trim().length < 5) {
      return 'low';
    }

    if (!azureConfig?.endpoint || !azureConfig?.apiKey) {
      console.warn('⚠️ No Azure config - defaulting to medium');
      return 'medium';
    }

    console.log('🧠 LLM determining importance...');
    
    const data = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
              content: `Rate memory importance as HIGH, MEDIUM, or LOW.

HIGH - Critical information:
- Core identity (name, major life facts)
- Life-changing events or decisions
- Time-sensitive deadlines (exams, interviews)
- Deep personal values or beliefs
- Significant goals or dreams
- Major life changes

MEDIUM - Useful context:
- Preferences and interests
- Regular habits or routines
- Relationships and social info
- Work/study details
- Upcoming plans (not urgent)
- Personality traits

LOW - Minor details:
- Casual observations
- One-time mentions without context
- Vague or uncertain statements

Think: "How important is this for understanding and helping this person?"

Respond ONLY: HIGH or MEDIUM or LOW`
            },
            {
          role: 'user',
          content: memoryText
        }
      ],
      {
        temperature: 0.3,
        max_tokens: 10
      }
    );

    const rating = data.choices[0].message.content.trim().toLowerCase();
    
    if (['high', 'medium', 'low'].includes(rating)) {
      console.log(`✅ Importance: ${rating}`);
      return rating;
    }

    return 'medium';

  } catch (error) {
    console.error('❌ Importance determination failed:', error);
    return 'medium';
  }
}
/**
 * ✅ Save conversation summary when chat ends
 * Generates 2-3 sentence summary of what was discussed
 */
async saveConversationSummary(messages, azureConfig = null) {
  try {
    if (!this.isReady()) {
      console.warn('⚠️ Memory service not ready');
      return null;
    }

    const conversationMessages = messages.filter(msg => 
      (msg.type === 'user' || msg.type === 'bot') &&
      msg.content &&
      !msg.isLoading
    );

    if (conversationMessages.length < 5) {
      console.log('⏭️ Conversation too short to summarize');
      return null;
    }

    const recentMessages = conversationMessages.slice(-20);
    const conversationText = recentMessages
      .map(m => `${m.type === 'user' ? 'User' : 'AI'}: ${m.content}`)
      .join('\n');

    console.log('📝 Generating conversation summary...');

    const config = azureConfig || this.azureConfig;
    
    let summary;
    if (config && config.endpoint && config.apiKey) {
      try {
        const response = await fetch(
          `${config.endpoint}/openai/deployments/${config.deployment}/chat/completions?api-version=2024-02-15-preview`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'api-key': config.apiKey
            },
            body: JSON.stringify({
              messages: [
                {
                  role: 'system',
                  content: `Summarize this conversation in 2-3 sentences using "You" perspective.

CRITICAL RULES:
- Start with "You"
- Be specific about topics discussed
- Include key points and important context
- Make it useful for future recall of this conversation

Example:
✅ GOOD: "You discussed your upcoming biology exam on November 15th and strategies for studying organic chemistry. You mentioned feeling stressed about the amount of material and asked for help breaking it into manageable chunks."

❌ BAD: "The conversation was about studying." (too vague)`
                },
                {
                  role: 'user',
                  content: conversationText
                }
              ],
              temperature: 0.3,
              max_tokens: 200
            })
          }
        );

        if (response.ok) {
          const data = await response.json();
          summary = data.choices[0].message.content.trim();
          console.log('✅ LLM generated summary:', summary);
        } else {
          throw new Error('LLM summary failed');
        }
      } catch (llmError) {
        console.warn('⚠️ LLM summary failed, using fallback');
        summary = this.generateFallbackSummary(recentMessages);
      }
    } else {
      console.log('📌 Using fallback summary (no LLM config)');
      summary = this.generateFallbackSummary(recentMessages);
    }

    const topics = this.extractTopicsFromMessages(recentMessages);
    const now = new Date();

    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const summaryDoc = {
      preptuneUserId: this.preptuneUserId,
      userId: this.currentUser.uid,
      memory: `Recent conversation: ${summary}`,
      type: 'conversation_summary',
      category: 'chat_history',
      topic: 'conversation',
      importance: 'medium',
      confidence: 0.90,
      tags: ['conversation', 'summary', ...topics],
      
      messageCount: conversationMessages.length,
      conversationDate: new Date().toLocaleDateString(),
      
      // ✅ CRITICAL TIMESTAMP FIELDS:
      savedAt: Timestamp.fromDate(now),
      createdAt: serverTimestamp(),
      mentionedAt: Timestamp.fromDate(now),
      timestamp: now.toISOString(),
      dayOfWeek: now.toLocaleDateString('en-US', { weekday: 'long' }),
      fullDate: now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      
      isActive: true
    };

    const docRef = await addDoc(memoryRef, summaryDoc);
    
    console.log('✅ Conversation summary saved:', {
      summaryId: docRef.id,
      summary: summary.substring(0, 50) + '...',
      topics: topics
    });
    
    return { summaryId: docRef.id, summary, topics };

  } catch (error) {
    console.error('❌ Failed to save conversation summary:', error);
    return null;
  }
}


/**
 * Generate fallback summary without LLM
 */
generateFallbackSummary(messages) {
  const userMessages = messages.filter(m => m.type === 'user');
  const topics = this.extractTopicsFromMessages(messages);
  
  if (topics.length > 0) {
    return `Discussed ${topics.slice(0, 3).join(', ')} and had a casual conversation.`;
  }
  
  return `Had a casual conversation with ${userMessages.length} exchanges.`;
}

/**
 * Extract main topics from conversation
 */
extractTopicsFromMessages(messages) {
  const topicKeywords = {
    'sports': ['soccer', 'football', 'game', 'match', 'barcelona', 'messi', 'team'],
    'weather': ['weather', 'rain', 'sunny', 'temperature', 'forecast'],
    'food': ['pizza', 'eat', 'food', 'hungry', 'dinner', 'lunch'],
    'work': ['work', 'job', 'meeting', 'project', 'deadline', 'office'],
    'study': ['study', 'exam', 'test', 'homework', 'learn', 'quiz'],
    'health': ['health', 'pain', 'exercise', 'workout', 'doctor', 'medicine'],
    'entertainment': ['movie', 'show', 'watch', 'music', 'listen', 'podcast']
  };

  const allText = messages
    .map(m => m.content.toLowerCase())
    .join(' ');

  const detectedTopics = [];
  
  for (const [topic, keywords] of Object.entries(topicKeywords)) {
    if (keywords.some(keyword => allText.includes(keyword))) {
      detectedTopics.push(topic);
    }
  }

  return detectedTopics;
}

/**
 * ✅ GET SHORT-TERM MEMORIES (expiring in 7 days)
 */
async getShortTermMemories(limitCount = 10) {
  if (!this._initialized) {
    await this.initialize();
  }

  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready');
    return [];
  }

  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const q = query(
      memoryRef,
      where('timeframe', '==', 'SHORT_TERM'),
      where('isActive', '==', true),
      orderBy('mentionedAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const memories = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Check if expired
      if (data.expiresAt) {
        const expiry = data.expiresAt.toDate();
        const now = new Date();
        
        if (now > expiry) {
          console.log('⏰ Memory expired:', data.memory.substring(0, 30));
          return; // Skip expired
        }
      }
      
      memories.push({
        id: doc.id,
        ...data
      });
    });

    console.log(`✅ Retrieved ${memories.length} short-term memories (< 7 days)`);
    return memories;
  } catch (error) {
    console.error('❌ Failed to get short-term memories:', error.message);
    return [];
  }
}

/**
 * ✅ GET LONG-TERM MEMORIES (permanent)
 */
async getLongTermMemories(limitCount = 20) {
  if (!this._initialized) {
    await this.initialize();
  }

  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready');
    return [];
  }

  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const q = query(
      memoryRef,
      where('timeframe', '==', 'LONG_TERM'),
      where('isActive', '==', true),
      orderBy('mentionedAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const memories = [];

    querySnapshot.forEach((doc) => {
      memories.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`✅ Retrieved ${memories.length} long-term memories (permanent)`);
    return memories;
  } catch (error) {
    console.error('❌ Failed to get long-term memories:', error.message);
    return [];
  }
}

/**
 * ✅ CLEANUP EXPIRED MEMORIES (run periodically)
 */
async cleanupExpiredMemories() {
  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready');
    return 0;
  }

  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const now = new Date();
    const q = query(
      memoryRef,
      where('timeframe', '==', 'SHORT_TERM'),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const batch = writeBatch(this.db);
    let expiredCount = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      if (data.expiresAt) {
        const expiry = data.expiresAt.toDate();
        
        if (now > expiry) {
          batch.update(doc.ref, { isActive: false });
          expiredCount++;
        }
      }
    });

    if (expiredCount > 0) {
      await batch.commit();
      console.log(`✅ Cleaned up ${expiredCount} expired short-term memories`);
    } else {
      console.log('✅ No expired memories to cleanup');
    }

    return expiredCount;
  } catch (error) {
    console.error('❌ Failed to cleanup expired memories:', error.message);
    return 0;
  }
}

// ============================================
// CODE BLOCK 1: NEW FUNCTION - Intelligent Timeframe Calculator
// ADD THIS to ConversationMemoryService class
// ============================================

async calculateTimeframeIntelligently(userQuery) {
  if (!userQuery || userQuery.trim().length < 3) {
    console.log('⏭️ Query too short for timeframe analysis');
    return null;
  }

  try {
    console.log('🧠 LLM analyzing timeframe intelligently...');
    
    const now = new Date();
    
    const analysis = await SecureAPI.callAzureOpenAI([
      {
        role: 'system',
        content: `You are a timeframe understanding expert. Analyze this query and determine EXACTLY when the user is asking about.

CURRENT DATE/TIME: ${now.toLocaleString()}
TODAY IS: ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][now.getDay()]}

Your job: Understand the user's timeframe reference and calculate the exact date range they're asking about.

EXAMPLES OF TIMEFRAME UNDERSTANDING:
- "2 weeks ago" = 14 days back from today
- "last 2 weeks" = 14 days back from today
- "3 weeks ago" = 21 days back
- "last 3 weeks" = 21 days back
- "2 months ago" = ~60 days back
- "last 2 months" = ~60 days back
- "5 weeks ago" = 35 days back
- "Sunday" = most recent Sunday (if today is NOT Sunday, go back to last Sunday; if today IS Sunday, go back 7 days)
- "last Monday" = most recent Monday
- "this Friday" = upcoming Friday OR today if today is Friday

CALCULATE:
1. How many days back from today (${now.toDateString()})
2. The exact start date (00:00:00)
3. The exact end date (23:59:59)

BE PRECISE - User will rely on this accuracy.

Return ONLY valid JSON:
{
  "understood": true or false,
  "daysBackMin": number,
  "daysBackMax": number,
  "startDate": "YYYY-MM-DD HH:MM:SS",
  "endDate": "YYYY-MM-DD HH:MM:SS",
  "description": "what user is asking about",
  "confidence": 0.0-1.0,
  "interpretedAs": "how you understood it"
}`
      },
      {
        role: 'user',
        content: `User query: "${userQuery}"\n\nCurrent date: ${now.toLocaleString()}\n\nWhat date range should I search?`
      }
    ], {
      temperature: 0.3,
      max_tokens: 300
    });

    const responseText = analysis.choices[0].message.content.trim();
    console.log('📊 Raw LLM response:', responseText.substring(0, 150));

    const jsonMatch = responseText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.error('❌ Failed to parse LLM response');
      return null;
    }

    let result;
    try {
      result = JSON.parse(jsonMatch[0]);
    } catch (parseError) {
      console.error('❌ JSON parse failed:', parseError.message);
      return null;
    }

    if (!result.understood || result.confidence < 0.7) {
      console.log('⚠️ LLM not confident:', result.interpretedAs);
      return null;
    }

    const startDate = new Date(result.startDate);
    const endDate = new Date(result.endDate);

    console.log('✅ Timeframe calculated:', {
      description: result.description,
      daysBack: result.daysBackMin,
      confidence: (result.confidence * 100).toFixed(0) + '%',
      startDate: startDate.toDateString(),
      endDate: endDate.toDateString()
    });

    return {
      startDate,
      endDate,
      daysBackMin: result.daysBackMin,
      daysBackMax: result.daysBackMax,
      description: result.description,
      confidence: result.confidence,
      interpretedAs: result.interpretedAs
    };

  } catch (error) {
    console.error('❌ Timeframe analysis error:', error);
    return null;
  }
}

// ============================================
// CODE BLOCK 4: PARALLEL CONVERSATION RECONSTRUCTOR
// Reconstruct full dialogue in parallel (microseconds)
// Add this to ConversationMemoryService class
// ============================================


async reconstructConversationIntelligently(memories, userQuery) {
  if (!memories || memories.length === 0) {
    console.log('📭 No memories to reconstruct');
    return null;
  }

  try {
    console.log('🚀 Starting parallel conversation reconstruction...');
    const startTime = performance.now();

    // ============================================
    // PARALLEL STEP 1: Infer Claude's responses
    // What did Claude probably say in response?
    // ============================================
    const inferResponsesPromise = SecureAPI.callAzureOpenAI([
      {
        role: 'system',
        content: `For each user statement, infer what Claude (the AI) likely responded with.

User memories show what they said. Infer natural AI responses.

CRITICAL: Be natural and conversational. Don't be robotic.

Return ONLY JSON:
{
  "inferred": [
    {
      "userMemoryIndex": 1,
      "userSaid": "exact user memory",
      "claudeLikelyResponded": "what Claude probably said based on context",
      "confidence": 0.9
    }
  ]
}`
      },
      {
        role: 'user',
        content: memories.map((m, idx) => `${idx + 1}. User said: "${m.memory}"`).join('\n')
      }
    ], {
      temperature: 0.4,
      max_tokens: 600
    });

    // ============================================
    // PARALLEL STEP 2: Group memories by theme
    // Find patterns and relationships
    // ============================================
    const themeGroupingPromise = SecureAPI.callAzureOpenAI([
      {
        role: 'system',
        content: `Analyze these user memories. What are the main topics/themes discussed?

Group by theme. Be specific about what was discussed.

Return ONLY JSON:
{
  "themes": [
    {
      "theme": "name",
      "subtopics": ["what was discussed"],
      "description": "brief description"
    }
  ]
}`
      },
      {
        role: 'user',
        content: memories.map((m, idx) => `${idx + 1}. ${m.memory}`).join('\n')
      }
    ], {
      temperature: 0.3,
      max_tokens: 300
    });

    // ============================================
    // WAIT FOR BOTH TO COMPLETE IN PARALLEL
    // ============================================
    console.log('⚡ Running 2 LLM analyses in PARALLEL...');
    const [inferenceResult, themeResult] = await Promise.all([
      inferResponsesPromise,
      themeGroupingPromise
    ]);

    const parallelTime = performance.now() - startTime;
    console.log(`⚡ Parallel analysis complete in ${Math.round(parallelTime)}ms`);

    // ============================================
    // PARSE RESULTS
    // ============================================
    let inferred, themes;

    try {
      const inferText = inferenceResult.choices[0].message.content.trim();
      const inferMatch = inferText.match(/\{[\s\S]*\}/);
      inferred = JSON.parse(inferMatch[0]);
    } catch (e) {
      console.warn('⚠️ Inference parse failed');
      inferred = { inferred: [] };
    }

    try {
      const themeText = themeResult.choices[0].message.content.trim();
      const themeMatch = themeText.match(/\{[\s\S]*\}/);
      themes = JSON.parse(themeMatch[0]);
    } catch (e) {
      console.warn('⚠️ Theme parse failed');
      themes = { themes: [] };
    }

    // ============================================
    // BUILD DIALOGUE (NATURAL CONVERSATION FLOW)
    // ============================================
    console.log('📝 Building natural dialogue...');

    const dialogue = memories.map((mem, idx) => {
      const inferredResponse = inferred.inferred?.find(i => i.userMemoryIndex === idx + 1);

      return {
        order: idx + 1,
        userSaid: mem.memory,
        claudeSaid: inferredResponse?.claudeLikelyResponded || '[Claude responded]',
        confidence: inferredResponse?.confidence || 0.7
      };
    });

    // ============================================
    // GENERATE NATURAL NARRATIVE (CONVERSATIONAL)
    // ============================================
    console.log('📖 Generating natural narrative...');

    const narrativePrompt = `Create a natural, conversational summary of this chat.

User's statements:
${memories.map((m, idx) => `${idx + 1}. "${m.memory}"`).join('\n')}

Inferred Claude responses:
${dialogue.map((d, idx) => `${idx + 1}. Claude: "${d.claudeSaid}"`).join('\n')}

RULES:
1. Write like it's a casual conversation flow
2. NO timestamps, NO dates, NO emojis, NO dividers
3. NO "According to my records..."
4. Just describe what was talked about naturally
5. Use paragraphs, not bullet points
6. Sound like a real person remembering a chat

Example of GOOD output:
"So we were talking about your schedule. You mentioned you have a class on Wednesdays, and I asked if you had other days you usually study. We also chatted about how you like to stay focused when you're into something."

Example of BAD output:
"📝 CONVERSATION BREAKDOWN
━━━━━━━━━━━━━━━━━━━━
You: [date] You mentioned having a class...
Me: [timestamp] I asked if you have other days...
⏱️ Completed in 5234ms"

Generate natural summary:`;

    const narrativeResponse = await SecureAPI.callAzureOpenAI([
      { role: 'system', content: narrativePrompt },
      { role: 'user', content: 'Generate natural dialogue summary' }
    ], {
      temperature: 0.7,
      max_tokens: 400
    });

    let narrative = narrativeResponse.choices[0].message.content.trim();
    // Remove any timestamps that might sneak in
    narrative = narrative
      .replace(/⏱️.*$/gm, '')
      .replace(/\━+/g, '')
      .replace(/📝|📌|✅|⚡/g, '')
      .replace(/\(.*completed.*ms.*\)/gi, '')
      .trim();

    const totalTime = performance.now() - startTime;
    console.log(`✅ Reconstruction complete in ${Math.round(totalTime)}ms`);

    return {
      narrative,
      dialogue,
      themes: themes.themes || [],
      timing: {
        total: Math.round(totalTime),
        parallelAnalysis: Math.round(parallelTime),
        method: 'parallel_llm'
      }
    };

  } catch (error) {
    console.error('❌ Reconstruction error:', error);
    return null;
  }
}


// ============================================
// CODE BLOCK 5: REPLACE intelligentMemoryRecall
// WITH THIS UPDATED VERSION
// Now includes conversation reconstruction
// ============================================

async intelligentMemoryRecall(userQuery, conversationMessages = []) {
  if (!this.isReady()) {
    console.warn('⚠️ Memory service not ready');
    return null;
  }

  try {
    console.log('🧠 Adaptive Memory Recall - Query:', userQuery);

    // ============================================
    // STEP 1: LLM DECIDES - ASK OR SEARCH?
    // ============================================
    const decisionAnalysis = await SecureAPI.callAzureOpenAI([
      {
        role: 'system',
        content: `Analyze the user's memory query. Decide if you should:
1. **ASK for clarity** - ONLY if they gave NO timeframe AND NO topic
2. **SEARCH** - If specific enough

WHEN TO ASK (TRULY VAGUE):
- "what did we talk about" → No timeframe, no topic - ASK
- "remember something" → TOO VAGUE - ASK

WHEN TO JUST SEARCH (they gave TIMEFRAME or TOPIC):
- "what did we talk about 2 weeks ago" → Has timeframe - SEARCH
- "didn't we talk about Python" → Has topic - SEARCH
- "about that job interview" → Has topic - SEARCH
- "into details of what i said" → Wants RECONSTRUCTION - SEARCH then RECONSTRUCT

Return ONLY valid JSON:
{
  "action": "ask|search",
  "wantsDetails": true or false,
  "wantsReconstruction": true or false,
  "clarityNeeded": "none|topic|timeframe|both",
  "clarityQuestion": null or "question string",
  "confidence": 0.85
}`
      },
      { role: 'user', content: userQuery }
    ], {
      temperature: 0.4,
      max_tokens: 300
    });

    let decisionText = decisionAnalysis.choices[0].message.content.trim();
    decisionText = decisionText.replace(/```json\n?|\n?```/g, '');

    console.log('📊 Raw decision response:', decisionText.substring(0, 150));

    let decision;
    try {
      let cleanedText = decisionText
        .replace(/,(\s*[}\]])/g, '$1')
        .replace(/:\s*'([^']*)'(?=\s*[,}\]])/g, ': "$1"')
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/\/\/.*/g, '')
        .trim();

      decision = JSON.parse(cleanedText);
      console.log('✅ Decision JSON parsed');
    } catch (parseError) {
      console.error('❌ Decision parse error');
      decision = {
        action: 'search',
        wantsDetails: false,
        wantsReconstruction: false,
        clarityNeeded: 'none',
        clarityQuestion: null,
        confidence: 0.5
      };
    }

    // ============================================
    // STEP 2: IF NEEDS CLARITY - ASK USER
    // ============================================
    if (decision.action === 'ask' && decision.clarityQuestion) {
      console.log('❓ Asking for clarity');
      return {
        found: false,
        needsClarity: true,
        clarityQuestion: decision.clarityQuestion,
        action: 'ask'
      };
    }

    // ============================================
    // STEP 3: INTELLIGENT TIMEFRAME CALCULATION
    // ============================================
    console.log('📅 Calculating timeframe intelligently...');

    const timeframeResult = await this.calculateTimeframeIntelligently(userQuery);
    
    let startDate, endDate;
    
    if (timeframeResult) {
      startDate = timeframeResult.startDate;
      endDate = timeframeResult.endDate;
      console.log('📅 Intelligent timeframe:', {
        description: timeframeResult.description,
        start: startDate.toDateString(),
        end: endDate.toDateString()
      });
    } else {
      // Fallback: Search last 60 days
      console.log('⚠️ Could not determine timeframe - defaulting to 60 days');
      const now = new Date();
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 60);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
    }

    // ============================================
    // STEP 4: SEARCH MEMORIES IN RANGE
    // ============================================
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    try {
      const q = query(
        memoryRef,
        where('mentionedAt', '>=', Timestamp.fromDate(startDate)),
        where('mentionedAt', '<=', Timestamp.fromDate(endDate)),
        where('isActive', '==', true),
        orderBy('mentionedAt', 'desc'),
        limit(150)
      );

      const snapshot = await getDocs(q);
      let allMemories = [];

      snapshot.forEach(doc => {
        allMemories.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`📚 Found ${allMemories.length} memories in range`);

      // ============================================
      // STEP 5: IF NOTHING FOUND - EXPAND SEARCH
      // ============================================
      if (allMemories.length === 0) {
        console.log('📭 No memories found - expanding to 6 months...');
        
        const now = new Date();
        const expandedStart = new Date(now);
        expandedStart.setDate(now.getDate() - 180);
        expandedStart.setHours(0, 0, 0, 0);
        
        const expandedQ = query(
          memoryRef,
          where('mentionedAt', '>=', Timestamp.fromDate(expandedStart)),
          where('isActive', '==', true),
          orderBy('mentionedAt', 'desc'),
          limit(150)
        );

        const expandedSnapshot = await getDocs(expandedQ);
        expandedSnapshot.forEach(doc => {
          allMemories.push({
            id: doc.id,
            ...doc.data()
          });
        });

        console.log(`📚 Expanded: Found ${allMemories.length} total memories`);
      }

      if (allMemories.length === 0) {
        return {
          found: false,
          reason: 'no_memories_found',
          action: 'no_match'
        };
      }

      // ============================================
      // STEP 6: LLM SEMANTIC FILTERING
      // ============================================
      console.log('🤖 LLM filtering for relevance...');

      const filterResponse = await SecureAPI.callAzureOpenAI([
        {
          role: 'system',
          content: `User query: "${userQuery}"

Found ${allMemories.length} memories from the timeframe. Filter to ONLY the ones that are relevant.

Return ONLY valid JSON:
{
  "relevantMemories": [
    {
      "index": 1,
      "matchType": "direct|topic|conceptual",
      "confidence": 0.95,
      "reason": "why this matches"
    }
  ],
  "totalRelevant": 3
}`
        },
        { 
          role: 'user', 
          content: allMemories
            .map((m, idx) => `${idx + 1}. ${m.memory}`)
            .join('\n')
        }
      ], {
        temperature: 0.4,
        max_tokens: 800
      });

      let filterText = filterResponse.choices[0].message.content.trim();
      filterText = filterText.replace(/```json\n?|\n?```/g, '');

      console.log('📊 Raw filter response:', filterText.substring(0, 150));

      let filtered;
      try {
        let cleanedFilter = filterText
          .replace(/,(\s*[}\]])/g, '$1')
          .replace(/:\s*'([^']*)'(?=\s*[,}\]])/g, ': "$1"')
          .replace(/\/\*[\s\S]*?\*\//g, '')
          .replace(/\/\/.*/g, '')
          .trim();

        filtered = JSON.parse(cleanedFilter);
        console.log('✅ Filter parsed:', filtered.totalRelevant, 'relevant');
      } catch (parseError) {
        console.error('❌ Filter parse error');
        filtered = {
          relevantMemories: allMemories.map((m, idx) => ({
            index: idx + 1,
            matchType: 'fallback',
            confidence: 0.5,
            reason: 'parse_failed'
          })),
          totalRelevant: allMemories.length
        };
      }

      // ============================================
      // STEP 7: NO RELEVANT MEMORIES
      // ============================================
      if (!filtered.relevantMemories || filtered.relevantMemories.length === 0) {
        console.log('❌ No relevant memories after filtering');
        return {
          found: false,
          reason: 'no_relevant_memories',
          searched: allMemories.length,
          action: 'no_match'
        };
      }

      // ============================================
      // STEP 8: GET MATCHED MEMORIES
      // ============================================
      const relevantMemories = filtered.relevantMemories
        .map(rm => {
          const idx = rm.index - 1;
          if (idx >= 0 && idx < allMemories.length) {
            return allMemories[idx];
          }
          return null;
        })
        .filter(Boolean)
        .slice(0, 10);

      console.log(`✅ Got ${relevantMemories.length} relevant memories`);

      // ============================================
      // STEP 9: CHECK IF USER WANTS RECONSTRUCTION
      // ============================================
      let reconstructedConversation = null;
      
      if (decision.wantsReconstruction || decision.wantsDetails) {
        console.log('🔄 User wants details - reconstructing conversation...');
        
        // ✅ PARALLEL RECONSTRUCTION (happens while building response)
        const reconstructionPromise = this.reconstructConversationIntelligently(
          relevantMemories,
          userQuery
        );

        // ============================================
        // STEP 10: BUILD BASIC RESPONSE (FAST)
        // While reconstruction happens in parallel
        // ============================================
        const memoryDates = relevantMemories.map((m) => {
          const date = m.mentionedAt?.toDate?.() || m.savedAt?.toDate?.() || new Date();
          const timeAgo = this.getNaturalTimeAgo(date);
          
          return {
            timeAgo,
            memory: m.memory
          };
        });

        const memoryList = memoryDates
          .map((md, idx) => `${idx + 1}. [${md.timeAgo}] "${md.memory}"`)
          .join('\n');

        const responsePrompt = `User asked: "${userQuery}"

They want DETAILS of what was discussed.

Relevant memories:
${memoryList}

Generate a NATURAL response that:
- Summarizes what was discussed
- Mentions it will provide details below
- Sounds conversational

Example:
✅ "Yeah! We had a good chat about your Sundays and how you spend that time. Here's what we discussed in detail..."

Max 2 sentences.`;

        const basicResponse = await SecureAPI.callAzureOpenAI([
          { role: 'system', content: responsePrompt },
          { role: 'user', content: 'Generate response' }
        ], {
          temperature: 0.8,
          max_tokens: 100
        });

        let response = basicResponse.choices[0].message.content.trim();
        response = response.replace(/\*\*/g, '').trim();

        // ✅ NOW WAIT FOR RECONSTRUCTION TO COMPLETE
        reconstructedConversation = await reconstructionPromise;

        console.log('✅ Reconstruction complete');

        if (reconstructedConversation) {
          return {
            found: true,
            memories: relevantMemories,
            response: response,
            reconstructedConversation: {
              narrative: reconstructedConversation.narrative,
              dialogue: reconstructedConversation.dialogue,
              themes: reconstructedConversation.themes,
              timing: reconstructedConversation.timing
            },
            totalFound: relevantMemories.length,
            confidence: 0.85,
            method: 'intelligent_recall_with_reconstruction',
            action: 'found_with_details'
          };
        }
      }

      // ============================================
      // STEP 11: SIMPLE RESPONSE (NO RECONSTRUCTION)
      // ============================================
      const memoryDates = relevantMemories.map((m) => {
        const date = m.mentionedAt?.toDate?.() || m.savedAt?.toDate?.() || new Date();
        const timeAgo = this.getNaturalTimeAgo(date);
        
        return {
          timeAgo,
          memory: m.memory
        };
      });

      const memoryList = memoryDates
        .map((md, idx) => `${idx + 1}. [${md.timeAgo}] "${md.memory}"`)
        .join('\n');

      const responsePrompt = `User asked: "${userQuery}"

Found these relevant memories:
${memoryList}

Generate a NATURAL, CONFIDENT response:
- Sound like you remember naturally
- Use the times provided
- 2-4 sentences max
- NO hedging ("maybe", "possibly")
- Confirm you found these

Examples:
✅ "Yeah! A few weeks ago you mentioned you wanted to learn Python. How's that going?"
✅ "Yesterday you were talking about your upcoming exam. Did it go okay?"`;

      const naturalResponse = await SecureAPI.callAzureOpenAI([
        { role: 'system', content: responsePrompt },
        { role: 'user', content: 'Generate response' }
      ], {
        temperature: 0.8,
        max_tokens: 200
      });

      let response = naturalResponse.choices[0].message.content.trim();
      response = response
        .replace(/\*\*/g, '')
        .replace(/\n\n+/g, ' ')
        .trim();

      console.log('✅ Response generated');

      return {
        found: true,
        memories: relevantMemories,
        response: response,
        totalFound: relevantMemories.length,
        confidence: 0.85,
        method: 'intelligent_recall',
        action: 'found'
      };

    } catch (dbError) {
      console.error('❌ Database error:', dbError);
      return {
        found: false,
        error: dbError.message,
        action: 'error'
      };
    }

  } catch (error) {
    console.error('❌ Recall error:', error);
    return { 
      found: false, 
      error: error.message,
      action: 'error'
    };
  }
}

// ============================================
// CODE BLOCK 6: HELPER FUNCTION
// (Same as before - for reference)
// ============================================

getNaturalTimeAgo(timestamp) {
  if (!timestamp) return 'recently';
  
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    
    const memoryDate = new Date(date);
    memoryDate.setHours(0, 0, 0, 0);
    
    const diffMs = today - memoryDate;
    const daysAgo = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    const hours = date.getHours();
    const timeOfDay = hours >= 5 && hours < 12 ? 'morning' :
                      hours >= 12 && hours < 17 ? 'afternoon' :
                      hours >= 17 && hours < 21 ? 'evening' : 'night';
    
    if (daysAgo === 0) {
      return `this ${timeOfDay}`;
    }
    
    if (daysAgo === 1) {
      return `yesterday ${timeOfDay}`;
    }
    
    if (daysAgo >= 2 && daysAgo < 7) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      return `${dayName} ${timeOfDay}`;
    }
    
    if (daysAgo >= 7 && daysAgo < 14) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      return `last ${dayName}`;
    }
    
    if (daysAgo >= 14 && daysAgo < 30) {
      const weeksAgo = Math.floor(daysAgo / 7);
      return `${weeksAgo} week${weeksAgo > 1 ? 's' : ''} ago`;
    }
    
    if (daysAgo >= 30 && daysAgo < 90) {
      const monthsAgo = Math.floor(daysAgo / 30);
      return `${monthsAgo} month${monthsAgo > 1 ? 's' : ''} ago`;
    }
    
    const monthsAgo = Math.floor(daysAgo / 30);
    if (monthsAgo < 12) {
      return `${monthsAgo} months ago`;
    } else {
      const yearsAgo = Math.floor(monthsAgo / 12);
      return `${yearsAgo} year${yearsAgo !== 1 ? 's' : ''} ago`;
    }
    
  } catch (error) {
    return 'recently';
  }
}
// ============================================
// CODE BLOCK 3: HELPER FUNCTION
// Add this to ConversationMemoryService class
// ============================================

getNaturalTimeAgo(timestamp) {
  if (!timestamp) return 'recently';
  
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    
    const memoryDate = new Date(date);
    memoryDate.setHours(0, 0, 0, 0);
    
    const diffMs = today - memoryDate;
    const daysAgo = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    const hours = date.getHours();
    const timeOfDay = hours >= 5 && hours < 12 ? 'morning' :
                      hours >= 12 && hours < 17 ? 'afternoon' :
                      hours >= 17 && hours < 21 ? 'evening' : 'night';
    
    // TODAY
    if (daysAgo === 0) {
      return `this ${timeOfDay}`;
    }
    
    // YESTERDAY
    if (daysAgo === 1) {
      return `yesterday ${timeOfDay}`;
    }
    
    // THIS WEEK
    if (daysAgo >= 2 && daysAgo < 7) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      return `${dayName} ${timeOfDay}`;
    }
    
    // LAST WEEK
    if (daysAgo >= 7 && daysAgo < 14) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      return `last ${dayName}`;
    }
    
    // WEEKS/MONTHS
    if (daysAgo >= 14 && daysAgo < 30) {
      const weeksAgo = Math.floor(daysAgo / 7);
      return `${weeksAgo} week${weeksAgo > 1 ? 's' : ''} ago`;
    }
    
    if (daysAgo >= 30 && daysAgo < 90) {
      const monthsAgo = Math.floor(daysAgo / 30);
      return `${monthsAgo} month${monthsAgo > 1 ? 's' : ''} ago`;
    }
    
    const monthsAgo = Math.floor(daysAgo / 30);
    if (monthsAgo < 12) {
      return `${monthsAgo} months ago`;
    } else {
      const yearsAgo = Math.floor(monthsAgo / 12);
      return `${yearsAgo} year${yearsAgo !== 1 ? 's' : ''} ago`;
    }
    
  } catch (error) {
    return 'recently';
  }
}

/**
 * ============================================
 * ADD THIS NEW HELPER FUNCTION
 * Detects if user is following up on recent conversation
 * ============================================
 */

isConversationFollowUp(userQuery, conversationMessages = []) {
  if (!conversationMessages || conversationMessages.length < 2) {
    return false;
  }

  const userQueryLower = userQuery.toLowerCase();
  
  // ============================================
  // PATTERN 1: "what date", "when was", "what time"
  // ============================================
  if (/what date|when was|when did|how long ago|what time|what day/.test(userQueryLower)) {
    const recentMessages = conversationMessages.slice(-5);
    const hasRecentContext = recentMessages.some(m => 
      m.content && m.content.length > 20
    );
    
    if (hasRecentContext) {
      console.log('✅ Follow-up detected: Asking for date/time of recent topic');
      return true;
    }
  }
  
  // ============================================
  // PATTERN 2: "it", "that", "this" + specific question
  // ============================================
  if (/^(like\s|that|this|it\s)/.test(userQueryLower)) {
    const recentMessages = conversationMessages.slice(-4);
    const hasMentionedTopic = recentMessages.some(m => 
      m.content && 
      m.type === 'bot' &&
      (m.content.includes('ago') || m.content.includes('mentioned') || m.content.includes('day'))
    );
    
    if (hasMentionedTopic) {
      console.log('✅ Follow-up detected: Using "like/that/this" to refer to mentioned topic');
      return true;
    }
  }
  
  // ============================================
  // PATTERN 3: "the date", "that day", "that memory"
  // ============================================
  if (/the date|the time|that day|that memory|that conversation/.test(userQueryLower)) {
    const recentMessages = conversationMessages.slice(-3);
    const hasPreviousMemory = recentMessages.some(m => 
      m.content && 
      (m.content.includes('ago') || m.content.includes('mentioned') || m.content.includes('morning'))
    );
    
    if (hasPreviousMemory) {
      console.log('✅ Follow-up detected: Asking for details about mentioned memory');
      return true;
    }
  }
  
  // ============================================
  // PATTERN 4: Double/triple question marks
  // ============================================
  if (/\?\?|\?\?\?/.test(userQueryLower)) {
    const recentMessages = conversationMessages.slice(-3);
    const hasContext = recentMessages.some(m => 
      m.type === 'bot' && m.content.length > 30
    );
    
    if (hasContext) {
      console.log('✅ Follow-up detected: Double/triple question marks suggest follow-up');
      return true;
    }
  }
  
  return false;
}

/**
 * ============================================
 * NEW FUNCTION: Analyze timeframe in query
 * ============================================
 */
analyzeTimeframeInQuery(userQuery) {
  const now = new Date();
  let searchStartDate = null;
  let searchEndDate = null;

  // TODAY
  if (/today|this morning|this afternoon|tonight/i.test(userQuery)) {
    searchStartDate = new Date(now);
    searchStartDate.setHours(0, 0, 0, 0);
    searchEndDate = new Date(now);
    searchEndDate.setHours(23, 59, 59, 999);
    return { timeframe: 'today', startDate: searchStartDate, endDate: searchEndDate };
  }

  // YESTERDAY
  if (/yesterday/i.test(userQuery)) {
    searchStartDate = new Date(now);
    searchStartDate.setDate(now.getDate() - 1);
    searchStartDate.setHours(0, 0, 0, 0);
    searchEndDate = new Date(searchStartDate);
    searchEndDate.setHours(23, 59, 59, 999);
    return { timeframe: 'yesterday', startDate: searchStartDate, endDate: searchEndDate };
  }

  // THIS WEEK
  if (/this week/i.test(userQuery)) {
    searchStartDate = new Date(now);
    const day = searchStartDate.getDay();
    const diff = searchStartDate.getDate() - day + (day === 0 ? -6 : 1);
    searchStartDate.setDate(diff);
    searchStartDate.setHours(0, 0, 0, 0);
    searchEndDate = new Date(now);
    searchEndDate.setHours(23, 59, 59, 999);
    return { timeframe: 'this week', startDate: searchStartDate, endDate: searchEndDate };
  }

  // LAST WEEK
  if (/last week/i.test(userQuery)) {
    searchStartDate = new Date(now);
    searchStartDate.setDate(now.getDate() - 7);
    const day = searchStartDate.getDay();
    const diff = searchStartDate.getDate() - day + (day === 0 ? -6 : 1);
    searchStartDate.setDate(diff);
    searchStartDate.setHours(0, 0, 0, 0);
    searchEndDate = new Date(searchStartDate);
    searchEndDate.setDate(searchEndDate.getDate() + 6);
    searchEndDate.setHours(23, 59, 59, 999);
    return { timeframe: 'last week', startDate: searchStartDate, endDate: searchEndDate };
  }

  // N DAYS AGO
  const daysMatch = userQuery.match(/(\d+)\s*days?\s*ago/i);
  if (daysMatch) {
    const days = parseInt(daysMatch[1]);
    searchStartDate = new Date(now);
    searchStartDate.setDate(now.getDate() - days);
    searchStartDate.setHours(0, 0, 0, 0);
    searchEndDate = new Date(now);
    searchEndDate.setHours(23, 59, 59, 999);
    return { timeframe: `${days} days ago`, startDate: searchStartDate, endDate: searchEndDate };
  }

  // N WEEKS AGO
  const weeksMatch = userQuery.match(/(\d+)\s*weeks?\s*ago/i);
  if (weeksMatch) {
    const weeks = parseInt(weeksMatch[1]);
    searchStartDate = new Date(now);
    searchStartDate.setDate(now.getDate() - (weeks * 7));
    searchStartDate.setHours(0, 0, 0, 0);
    searchEndDate = new Date(now);
    searchEndDate.setHours(23, 59, 59, 999);
    return { timeframe: `${weeks} weeks ago`, startDate: searchStartDate, endDate: searchEndDate };
  }

  // N MONTHS AGO
  const monthsMatch = userQuery.match(/(\d+)\s*months?\s*ago/i);
  if (monthsMatch) {
    const months = parseInt(monthsMatch[1]);
    searchStartDate = new Date(now);
    searchStartDate.setMonth(now.getMonth() - months);
    searchStartDate.setHours(0, 0, 0, 0);
    searchEndDate = new Date(now);
    searchEndDate.setHours(23, 59, 59, 999);
    return { timeframe: `${months} months ago`, startDate: searchStartDate, endDate: searchEndDate };
  }

  // N YEARS AGO
  const yearsMatch = userQuery.match(/(\d+)\s*years?\s*ago/i);
  if (yearsMatch) {
    const years = parseInt(yearsMatch[1]);
    searchStartDate = new Date(now);
    searchStartDate.setFullYear(now.getFullYear() - years);
    searchStartDate.setHours(0, 0, 0, 0);
    searchEndDate = new Date(now);
    searchEndDate.setHours(23, 59, 59, 999);
    return { timeframe: `${years} years ago`, startDate: searchStartDate, endDate: searchEndDate };
  }

  // No timeframe detected
  return null;
}
/**
 * ============================================
 * NEW FUNCTION: Search memories by date range
 * ============================================
 */
async searchMemoriesByDateRange(startDate, endDate) {
  if (!this.isReady()) {
    return [];
  }

  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const q = query(
      memoryRef,
      where('mentionedAt', '>=', Timestamp.fromDate(startDate)),
      where('mentionedAt', '<=', Timestamp.fromDate(endDate)),
      where('isActive', '==', true),
      orderBy('mentionedAt', 'desc'),
      limit(50)
    );

    const snapshot = await getDocs(q);
    const memories = [];

    snapshot.forEach((doc) => {
      memories.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`✅ Found ${memories.length} memories between ${startDate.toDateString()} and ${endDate.toDateString()}`);
    
    return memories;

  } catch (error) {
    console.error('❌ Date range search error:', error);
    return [];
  }
}

/**
 * ============================================
 * ENHANCED: formatMemoryRecallResponse with dates
 * ============================================
 */
async formatMemoryRecallResponse(memories, timeframeAnalysis, userQuery, azureConfig) {
  if (!memories || memories.length === 0) {
    return null;
  }

  try {
    console.log(`📝 Formatting ${memories.length} memories into response...`);

    // If only 1-2 memories, show them directly
    if (memories.length <= 2) {
      const memoryTexts = memories
        .map(m => {
          const timeAgo = this.getNaturalTimeAgo(m.mentionedAt);
          return `- ${m.memory} (${timeAgo})`;
        })
        .join('\n');

      const response = `Yeah! ${memoryTexts}`;
      console.log('✅ Simple response for 1-2 memories');
      return response;
    }

    // Multiple memories - use LLM to summarize
    if (!azureConfig?.endpoint || !azureConfig?.apiKey) {
      const memoryList = memories
        .slice(0, 5)
        .map((m, i) => `${i + 1}. ${m.memory}`)
        .join('\n');
      console.log('✅ Fallback list response');
      return `We talked about:\n\n${memoryList}`;
    }

    // Use LLM to create natural summary
    const memoryList = memories
      .slice(0, 7)
      .map(m => {
        const timeAgo = this.getNaturalTimeAgo(m.mentionedAt);
        return `- ${m.memory} (${timeAgo})`;
      })
      .join('\n');

    const response = await fetch(
      `${azureConfig.endpoint}/openai/deployments/${azureConfig.deployment}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': azureConfig.apiKey
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `You're helping recall what was discussed. Create a natural, conversational response.

RULES:
1. Sound like a friend remembering, not a database
2. Group related topics together
3. Include time references naturally
4. Keep it 2-4 sentences max
5. Add a natural follow-up question

${timeframeAnalysis ? `The user asked about: ${timeframeAnalysis.timeframe}` : ''}

EXAMPLES:
✅ "Yeah! A few weeks ago you were thinking about learning Python, and you also mentioned wanting to get back into running."
✅ "Last week we talked about your exam coming up and how stressed you were. Did it go okay?"
✅ "We discussed your new job and your upcoming trip. How's everything going?"

Generate a natural response about what was discussed.`,
              temperature: 0.7,
              max_tokens: 150
            },
            {
              role: 'user',
              content: `User asked: "${userQuery}"\n\nWhat we discussed:\n${memoryList}`
            }
          ]
        })
      }
    );

    if (!response.ok) {
      console.error('❌ Formatting API error:', response.status);
      return null;
    }

    const data = await response.json();
    const formattedResponse = data.choices[0]?.message?.content?.trim();

    if (formattedResponse) {
      console.log('✅ LLM-formatted response');
      return formattedResponse;
    }

    return null;

  } catch (error) {
    console.error('❌ Format error:', error);
    return null;
  }
}

/**
 * Analyze user's query to understand what they're looking for
 * FIXED: Better timeframe detection
 */
async analyzeMemoryQuery(userQuery, azureConfig) {
  try {
    // ============================================
    // STEP 1: Quick pattern matching FIRST
    // (Before calling LLM - faster and more reliable)
    // ============================================
    
    const queryLower = userQuery.toLowerCase();
    
    let detectedTimeframe = null;
    let confidence = 0;
    
    // YESTERDAY
    if (queryLower.includes('yesterday')) {
      detectedTimeframe = 'yesterday';
      confidence = 0.98;
    }
    // TODAY / THIS MORNING / THIS AFTERNOON
    else if (queryLower.includes('today') || queryLower.includes('this morning') || queryLower.includes('this afternoon')) {
      detectedTimeframe = 'today';
      confidence = 0.98;
    }
    // LAST WEEK
    else if (queryLower.includes('last week')) {
      detectedTimeframe = 'last week';
      confidence = 0.98;
    }
    // THIS WEEK
    else if (queryLower.includes('this week')) {
      detectedTimeframe = 'this week';
      confidence = 0.95;
    }
    // FEW DAYS AGO / COUPLE DAYS AGO
    else if (queryLower.includes('few days') || queryLower.includes('couple days') || queryLower.includes('several days')) {
      detectedTimeframe = 'few days ago';
      confidence = 0.90;
    }
    // FEW HOURS AGO
    else if (queryLower.includes('few hours') || queryLower.includes('couple hours')) {
      detectedTimeframe = 'few hours ago';
      confidence = 0.90;
    }
    // FEW MINUTES AGO / JUST NOW
    else if (queryLower.includes('few minutes') || queryLower.includes('just now') || queryLower.includes('a moment ago')) {
      detectedTimeframe = 'few minutes ago';
      confidence = 0.90;
    }
    // LAST MONTH
    else if (queryLower.includes('last month')) {
      detectedTimeframe = 'last month';
      confidence = 0.95;
    }
    // THIS MONTH
    else if (queryLower.includes('this month')) {
      detectedTimeframe = 'this month';
      confidence = 0.95;
    }
    // LAST YEAR / YEAR AGO
    else if (queryLower.includes('last year') || queryLower.includes('year ago')) {
      detectedTimeframe = 'last year';
      confidence = 0.95;
    }
    
    // ============================================
    // If we got a strong match via pattern, use it
    // ============================================
    if (detectedTimeframe && confidence >= 0.90) {
      console.log('✅ Timeframe detected via pattern matching:', {
        timeframe: detectedTimeframe,
        confidence: confidence
      });
      
      return {
        isMemoryQuery: true,
        timeframe: detectedTimeframe,
        specificDate: null,
        topics: [],
        queryType: 'recall',
        confidence: confidence,
        source: 'pattern_matching'
      };
    }
    
    // ============================================
    // STEP 2: Fall back to LLM for complex cases
    // ============================================
    
    if (!azureConfig?.endpoint || !azureConfig?.apiKey) {
      console.warn('⚠️ No Azure config, returning basic analysis');
      return { isMemoryQuery: false };
    }

    const response = await fetch(
      `${azureConfig.endpoint}/openai/deployments/${azureConfig.deployment}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': azureConfig.apiKey
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `Analyze if user is asking about past memories/conversations.

Detect queries like:
✅ "remember when I said..."
✅ "what did I tell you about..."
✅ "2 weeks ago I mentioned..."
✅ "remind me what I said about soccer"
✅ "didn't I tell you I had an exam?"
✅ "last Friday I talked about..."

Extract:
1. isMemoryQuery: true/false
2. timeframe: One of these EXACT values:
   - "yesterday"
   - "today"
   - "few minutes ago"
   - "few hours ago"
   - "few days ago"
   - "this week"
   - "last week"
   - "this month"
   - "last month"
   - "this year"
   - "last year"
   - "unspecified" (if no timeframe mentioned)

3. specificDate: day of week if mentioned (e.g., "Friday", "Tuesday"), else null
4. topics: what keywords/topics to search for
5. queryType: "recall" (remember when), "verification" (did I tell you), or "search" (what did I say)

CRITICAL: The timeframe MUST be one of the exact values above. Don't make up new values.

Return ONLY valid JSON:
{
  "isMemoryQuery": true,
  "timeframe": "yesterday",
  "specificDate": null,
  "topics": [],
  "queryType": "recall",
  "confidence": 0.95
}`
            },
            {
              role: 'user',
              content: userQuery
            }
          ],
          temperature: 0.1,
          max_tokens: 200
        })
      }
    );

    if (!response.ok) {
      console.error('❌ Analysis API error:', response.status);
      return { isMemoryQuery: false };
    }

    const data = await response.json();
    const content = data.choices[0].message.content.trim();
    
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.error('❌ Failed to parse analysis response');
      return { isMemoryQuery: false };
    }
    
    const analysis = JSON.parse(jsonMatch[0]);
    
    // ============================================
    // VALIDATE TIMEFRAME
    // ============================================
    const validTimeframes = [
      'yesterday', 'today', 'few minutes ago', 'few hours ago', 
      'few days ago', 'this week', 'last week', 'this month', 
      'last month', 'this year', 'last year', 'unspecified'
    ];
    
    if (!validTimeframes.includes(analysis.timeframe)) {
      console.warn(`⚠️ Invalid timeframe from LLM: "${analysis.timeframe}", using "unspecified"`);
      analysis.timeframe = 'unspecified';
    }
    
    console.log('🤖 AI decision:', {
      isMemoryQuery: analysis.isMemoryQuery,
      requestType: analysis.requestType,
      confidence: analysis.confidence,
      reasoning: analysis.reasoning,
      timeframe: analysis.timeframe
    });
    
    return analysis;
    
  } catch (error) {
    console.error('❌ Query analysis error:', error);
    return { isMemoryQuery: false };
  }
}


// ============================================
// FIX: Replace calculateDateRange() in ConversationMemorySystem.js
// ============================================

/**
 * Calculate date range from timeframe string
 * FIXED: Better handling of "yesterday", "last week", etc.
 */

calculateDateRange(timeframe) {
  if (!timeframe || timeframe === 'unspecified') {
    console.log('📭 No timeframe - searching all time');
    return { startDate: null, endDate: null };
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0); // Start of today
  
  let startDate = new Date(now);
  let endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999);

  const tf = timeframe.toLowerCase().trim();

  console.log('📅 calculateDateRange:', { 
    timeframe: tf, 
    today: now.toDateString(), 
    todayDayOfWeek: this.getDayName(now.getDay()) 
  });

  // ============================================
  // FIX: NAMED DAYS (Sunday, Monday, Tuesday, etc)
  // ============================================
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  
  for (let i = 0; i < dayNames.length; i++) {
    const dayName = dayNames[i];
    if (tf.includes(dayName)) {
      console.log(`✅ User mentioned: ${dayName}`);
      
      // Find the most recent occurrence of this day
      const targetDate = new Date(now);
      const currentDayOfWeek = now.getDay();
      let daysBackToTarget = (currentDayOfWeek - i) % 7;
      
      // If it's the same day, go back 7 days (last week's occurrence)
      if (daysBackToTarget === 0 && !tf.includes('this ' + dayName)) {
        daysBackToTarget = 7;
      }
      
      targetDate.setDate(now.getDate() - daysBackToTarget);
      targetDate.setHours(0, 0, 0, 0);
      
      const resultEndDate = new Date(targetDate);
      resultEndDate.setHours(23, 59, 59, 999);
      
      const daysBack = Math.ceil((now - targetDate) / (1000 * 60 * 60 * 24));
      
      console.log(`📅 ${dayName.toUpperCase()} RANGE:`, {
        daysBackToTarget,
        daysBack,
        start: targetDate.toDateString(),
        end: resultEndDate.toDateString()
      });
      
      return { startDate: targetDate, endDate: resultEndDate };
    }
  }

  // ============================================
  // TODAY
  // ============================================
  if (tf.includes('today') || tf.includes('this morning') || tf.includes('this afternoon')) {
    startDate = new Date(now);
    startDate.setHours(0, 0, 0, 0);
    endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);
    console.log('📅 TODAY range:', { start: startDate.toDateString(), end: endDate.toDateString() });
    return { startDate, endDate };
  }

  // ============================================
  // YESTERDAY
  // ============================================
  if (tf.includes('yesterday')) {
    startDate = new Date(now);
    startDate.setDate(startDate.getDate() - 1);
    startDate.setHours(0, 0, 0, 0);
    
    endDate = new Date(startDate);
    endDate.setHours(23, 59, 59, 999);
    
    console.log('📅 YESTERDAY range:', { start: startDate.toDateString(), end: endDate.toDateString() });
    return { startDate, endDate };
  }

  // ============================================
  // THIS WEEK
  // ============================================
  if (tf.includes('this week')) {
    startDate = new Date(now);
    const day = startDate.getDay();
    const diff = startDate.getDate() - day + (day === 0 ? -6 : 1);
    startDate.setDate(diff);
    startDate.setHours(0, 0, 0, 0);
    
    endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);
    
    console.log('📅 THIS WEEK range:', { start: startDate.toDateString(), end: endDate.toDateString() });
    return { startDate, endDate };
  }

  // ============================================
  // LAST WEEK
  // ============================================
  if (tf.includes('last week') || tf.includes('past week')) {
    startDate = new Date(now);
    startDate.setDate(now.getDate() - 7);
    const day = startDate.getDay();
    const diff = startDate.getDate() - day + (day === 0 ? -6 : 1);
    startDate.setDate(diff);
    startDate.setHours(0, 0, 0, 0);
    
    endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    endDate.setHours(23, 59, 59, 999);
    
    console.log('📅 LAST WEEK range:', { start: startDate.toDateString(), end: endDate.toDateString() });
    return { startDate, endDate };
  }

  // ============================================
  // DAYS AGO
  // ============================================
  const daysAgoMatch = tf.match(/(\d+)\s*days?\s*ago/i);
  if (daysAgoMatch) {
    const daysAgo = parseInt(daysAgoMatch[1]);
    startDate = new Date(now);
    startDate.setDate(startDate.getDate() - daysAgo);
    startDate.setHours(0, 0, 0, 0);
    
    endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);
    
    console.log('📅 N DAYS AGO range:', { daysAgo, start: startDate.toDateString(), end: endDate.toDateString() });
    return { startDate, endDate };
  }

  // ============================================
  // FEW MINUTES/HOURS/DAYS AGO
  // ============================================
  if (tf.includes('few minutes ago') || tf.includes('just now')) {
    startDate = new Date(now);
    startDate.setHours(startDate.getHours() - 1);
    endDate = new Date(now);
    console.log('📅 FEW MINUTES AGO range:', { start: startDate.toISOString(), end: endDate.toISOString() });
    return { startDate, endDate };
  }

  if (tf.includes('few hours ago')) {
    startDate = new Date(now);
    startDate.setHours(startDate.getHours() - 4);
    endDate = new Date(now);
    console.log('📅 FEW HOURS AGO range:', { start: startDate.toISOString(), end: endDate.toISOString() });
    return { startDate, endDate };
  }

  if (tf.includes('few days ago')) {
    startDate = new Date(now);
    startDate.setDate(startDate.getDate() - 3);
    startDate.setHours(0, 0, 0, 0);
    endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);
    console.log('📅 FEW DAYS AGO range:', { start: startDate.toDateString(), end: endDate.toDateString() });
    return { startDate, endDate };
  }

  // ============================================
  // MONTHS AGO
  // ============================================
  const monthsAgoMatch = tf.match(/(\d+)\s*months?\s*ago/i);
  if (monthsAgoMatch) {
    const monthsAgo = parseInt(monthsAgoMatch[1]);
    startDate = new Date(now);
    startDate.setMonth(startDate.getMonth() - monthsAgo);
    startDate.setHours(0, 0, 0, 0);
    endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);
    console.log('📅 N MONTHS AGO range:', { monthsAgo, start: startDate.toDateString(), end: endDate.toDateString() });
    return { startDate, endDate };
  }

  // ============================================
  // DEFAULT: Search all time
  // ============================================
  console.log('📭 Unknown timeframe - searching all time:', timeframe);
  return { startDate: null, endDate: null };
}

// ============================================
// HELPER: Get day name from index
// ============================================
getDayName(dayIndex) {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayIndex];
}

/**
 * Search memories with fallback if date range filtering fails
 */
async searchMemoriesWithAnalysis(analysis) {
  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    // Calculate date range based on timeframe
    const { startDate, endDate } = this.calculateDateRange(analysis.timeframe);
    
    let q;
    
    // ============================================
    // CASE 1: WITH DATE RANGE
    // ============================================
    if (startDate && endDate) {
      console.log('🔍 Searching with date range:', { 
        startDate: startDate.toISOString(), 
        endDate: endDate.toISOString() 
      });
      
      try {
        q = query(
          memoryRef,
          where('savedAt', '>=', Timestamp.fromDate(startDate)),
          where('savedAt', '<=', Timestamp.fromDate(endDate)),
          where('isActive', '==', true),
          orderBy('savedAt', 'desc'),
          limit(100)
        );
        
        const snapshot = await getDocs(q);
        console.log(`✅ Found ${snapshot.size} memories in date range`);
        
        if (snapshot.size > 0) {
          const memories = [];
          snapshot.forEach(doc => {
            memories.push({
              id: doc.id,
              ...doc.data()
            });
          });
          return this.filterByTopics(memories, analysis);
        }
      } catch (dateError) {
        console.warn('⚠️ Date range query failed, trying without dates:', dateError.message);
      }
    }

    // ============================================
    // CASE 2: FALLBACK - NO DATE FILTER (search all time)
    // ============================================
    console.log('🔍 Fallback: Searching all memories (no date filter)');
    
    q = query(
      memoryRef,
      where('isActive', '==', true),
      orderBy('savedAt', 'desc'),
      limit(200) // Search more when no date filter
    );
    
    const snapshot = await getDocs(q);
    console.log(`✅ Found ${snapshot.size} total active memories`);
    
    if (snapshot.size === 0) {
      console.log('📭 No memories at all');
      return [];
    }
    
    const memories = [];
    snapshot.forEach(doc => {
      memories.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // If no specific timeframe, return recent
    if (!analysis.timeframe || analysis.timeframe === 'unspecified') {
      console.log('📭 No timeframe specified, returning 5 most recent');
      return memories.slice(0, 5);
    }
    
    // Filter by topics if provided
    return this.filterByTopics(memories, analysis);

  } catch (error) {
    console.error('❌ Memory search failed:', error);
    return [];
  }
}

/**
 * Filter memories by topics
 */
filterByTopics(memories, analysis) {
  if (!analysis.topics || analysis.topics.length === 0) {
    console.log('📭 No topics to filter by, returning all');
    return memories;
  }

  const filtered = memories.filter(memory => {
    const memoryText = memory.memory.toLowerCase();
    const tags = (memory.tags || []).map(t => t.toLowerCase());
    
    return analysis.topics.some(topic => {
      const topicLower = topic.toLowerCase();
      return memoryText.includes(topicLower) || 
             tags.some(tag => tag.includes(topicLower));
    });
  });

  console.log(`✅ Filtered to ${filtered.length} memories matching topics:`, analysis.topics);
  return filtered;
}

/**
 * ✅ NEW: Get NATURAL time reference (how humans actually talk)
 */
getNaturalTimeAgo(timestamp) {
  if (!timestamp) return 'recently';
  
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    
    // ============================================
    // CRITICAL FIX: Use CALENDAR dates, not decimals
    // ============================================
    
    // Get CALENDAR dates (ignore time of day for comparison)
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    
    const memoryDate = new Date(date);
    memoryDate.setHours(0, 0, 0, 0);
    
    // Calculate days using EXACT calendar math
    const diffMs = today - memoryDate;
    const daysAgo = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    console.log(`📅 getNaturalTimeAgo: ${daysAgo} days ago (exact calculation)`);
    
    // Get time of day for the memory
    const hours = date.getHours();
    const timeOfDay = this.getTimeOfDayPhrase(hours);
    const timeString = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
    
    // ============================================
    // NOW USE EXACT daysAgo values instead of decimals
    // ============================================
    
    // TODAY (0 days ago)
    if (daysAgo === 0) {
      if (now.getHours() - hours < 1) {
        return 'just now';
      } else if (now.getHours() - hours < 4) {
        return `earlier ${timeOfDay}`;
      } else {
        return `this ${timeOfDay} around ${timeString}`;
      }
    }
    
    // YESTERDAY (1 day ago)
    if (daysAgo === 1) {
      return `yesterday ${timeOfDay}`;
    }
    
    // 2 DAYS AGO - FIX: Was being grouped as "couple weeks ago"
    if (daysAgo === 2) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      return `2 days ago on ${dayName} ${timeOfDay}`;
    }
    
    // 3 DAYS AGO
    if (daysAgo === 3) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      return `3 days ago on ${dayName} ${timeOfDay}`;
    }
    
    // THIS WEEK (4-6 days ago)
    if (daysAgo >= 4 && daysAgo < 7) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      return `${dayName} ${timeOfDay}`;
    }
    
    // LAST WEEK (7-13 days ago)
    if (daysAgo >= 7 && daysAgo < 14) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      return `last ${dayName}`;
    }
    
    // 2-3 WEEKS AGO (14-21 days) - FIX: Now accurate!
    if (daysAgo >= 14 && daysAgo < 21) {
      const weeksAgo = Math.floor(daysAgo / 7);
      return `${weeksAgo} week${weeksAgo > 1 ? 's' : ''} ago`;
    }
    
    // ABOUT 3 WEEKS AGO (21-30 days)
    if (daysAgo >= 21 && daysAgo < 30) {
      return `about 3 weeks ago`;
    }
    
    // LAST MONTH (30-45 days)
    if (daysAgo >= 30 && daysAgo < 45) {
      return `last month`;
    }
    
    // 1-3 MONTHS AGO (45-90 days)
    if (daysAgo >= 45 && daysAgo < 90) {
      const monthsAgo = Math.floor(daysAgo / 30);
      return `${monthsAgo} month${monthsAgo > 1 ? 's' : ''} ago`;
    }
    
    // 3+ MONTHS AGO
    const monthsAgo = Math.floor(daysAgo / 30);
    if (monthsAgo < 12) {
      return `${monthsAgo} months ago`;
    } else {
      const yearsAgo = Math.floor(monthsAgo / 12);
      return `${yearsAgo} year${yearsAgo !== 1 ? 's' : ''} ago`;
    }
    
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'recently';
  }
}




/**
 * ✅ NEW: Get precise time reference (only when user asks for specifics)
 */
getPreciseTimeReference(timestamp) {
  if (!timestamp) return 'unknown time';
  
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const monthDay = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const timeString = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
    const hours = date.getHours();
    const timeOfDay = this.getTimeOfDayPhrase(hours);
    
    // Include how long ago for context
    const hoursAgo = Math.round(diffHours);
    
    return `${hoursAgo} hours ago on ${dayName}, ${monthDay} at ${timeString} (${timeOfDay})`;
    
  } catch (error) {
    console.error('Error formatting precise time:', error);
    return 'unknown time';
  }
}

/**
 * ✅ NEW: Get natural time of day phrase
 */
getTimeOfDayPhrase(hour) {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

/**
 * ✅ NEW: LLM decides if user wants precise time details
 */
async isAskingForPreciseTime(userMessage, azureConfig) {
  if (!userMessage) return false;
  
  if (!azureConfig?.endpoint || !azureConfig?.apiKey) {
    return false; // Default to natural time
  }
  
  try {
    const response = await fetch(
      `${azureConfig.endpoint}/openai/deployments/${azureConfig.deployment}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': azureConfig.apiKey
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `Analyze if the user is asking for PRECISE/SPECIFIC time information.

✅ YES - User wants precise time:
- "exactly when did I say that?"
- "what time was it?"
- "what day was that?"
- "give me the exact date"
- "when specifically?"
- "what was the timestamp?"

❌ NO - User wants casual recall:
- "remember when I mentioned..."
- "didn't I tell you about..."
- "what did I say about..."
- "you remember that thing I said?"

Return ONLY valid JSON:
{
  "wants_precise_time": true or false,
  "confidence": 0.0 to 1.0
}`
            },
            {
              role: 'user',
              content: userMessage
            }
          ],
          temperature: 0.2,
          max_tokens: 100
        })
      }
    );

    if (response.ok) {
      const data = await response.json();
      const content = data.choices[0].message.content.trim();
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const result = JSON.parse(jsonMatch[0]);
        return result.wants_precise_time;
      }
    }
    
    return false;
    
  } catch (error) {
    console.error('❌ Precision check failed:', error);
    return false;
  }
}

/**
 * Format memories into natural language response
 */
async formatMemoryRecall(memories, analysis, userQuery, azureConfig) {
  try {
    // Check if user wants precise time using LLM
    const wantsPreciseTime = await this.isAskingForPreciseTime(userQuery, azureConfig);
    
    // Build memory list with appropriate time format
    const memoryList = memories.slice(0, 5).map((mem, idx) => {
      const timeRef = wantsPreciseTime 
        ? this.getPreciseTimeReference(mem.savedAt)
        : this.getNaturalTimeAgo(mem.savedAt);
      
      const discussing = mem.discussionTopics?.length > 0 
        ? ` (we were talking about ${mem.discussionTopics.slice(0, 2).join(' and ')})` 
        : '';

      return `${idx + 1}. ${timeRef}: "${mem.memory}"${discussing}`;
    }).join('\n');

    const response = await fetch(
      `${azureConfig.endpoint}/openai/deployments/${azureConfig.deployment}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': azureConfig.apiKey
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `Format memory recall naturally and conversationally.

User asked: "${userQuery}"
User wants precise time: ${wantsPreciseTime ? 'YES' : 'NO'}

Matching memories:
${memoryList}

CRITICAL RULES FOR TIME REFERENCES:

${wantsPreciseTime ? `
🎯 USER WANTS PRECISE TIME - BE SPECIFIC:
✅ GOOD: "That was 19 hours ago on Wednesday at 12:22 AM"
✅ GOOD: "2 weeks ago on Friday, January 5th at 3:15 PM"
✅ GOOD: "Last Tuesday, December 12th in the evening around 8:30 PM"
` : `
💬 USE NATURAL, CASUAL TIME REFERENCES:
✅ GOOD: "yesterday evening"
✅ GOOD: "this morning"
✅ GOOD: "last Friday afternoon"
✅ GOOD: "a couple weeks ago"
✅ GOOD: "earlier today"

❌ AVOID UNLESS ASKED:
❌ DON'T: "19 hours ago on Wednesday at 12:22 AM"
❌ DON'T: "2024-01-15 at 15:30"
❌ DON'T: "exactly 3.5 hours ago"
`}

OTHER RULES:
✅ Use "You" perspective: "You mentioned..." NOT "User mentioned..."
✅ Reference context: "when we were talking about X"
✅ Sound like a friend remembering, not a database
✅ Keep it SHORT (2-3 sentences max)

Examples of NATURAL responses:
- "Oh yeah, yesterday evening around 8pm you told me about that soccer game. You were pretty nervous about it!"
- "Last Friday afternoon you mentioned wanting to learn Python. How's that going?"
- "A couple weeks ago you said you love horror movies. Have you watched anything good lately?"

Examples when user wants PRECISE time:
- "That was 19 hours ago on Wednesday, January 3rd at 12:22 AM, when we were discussing your love of ice cream."
- "Exactly 2 weeks ago on Friday, January 5th at 3:15 PM, you mentioned having an exam the next day."`
            },
            {
              role: 'user',
              content: `Format response for: "${userQuery}"`
            }
          ],
          temperature: 0.7,
          max_tokens: 200
        })
      }
    );

    if (response.ok) {
      const data = await response.json();
      const naturalResponse = data.choices[0].message.content.trim();
      return naturalResponse;
    }

    // Fallback
    const firstMemory = memories[0];
    const timeAgo = wantsPreciseTime 
      ? this.getPreciseTimeReference(firstMemory.savedAt)
      : this.getNaturalTimeAgo(firstMemory.savedAt);
    return `Yeah! ${timeAgo} you mentioned: "${firstMemory.memory}"`;

  } catch (error) {
    console.error('❌ Format failed:', error);
    const firstMemory = memories[0];
    const timeAgo = this.getNaturalTimeAgo(firstMemory.savedAt);
    return `Yeah! ${timeAgo} you mentioned: "${firstMemory.memory}"`;
  }
}

/**
 * Get detailed time ago (seconds, minutes, hours, days, weeks, months, years)
 */
getDetailedTimeAgo(timestamp) {
  if (!timestamp) return 'some time ago';
  
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) {
      return `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''} ago`;
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else if (diffWeeks < 4) {
      return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
    } else if (diffMonths < 12) {
      return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
    }

  } catch (error) {
    console.error('Error formatting time:', error);
    return 'some time ago';
  }
}

/**
 * Basic memory search (fallback)
 */
async basicMemorySearch(searchTerm) {
  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const q = query(
      memoryRef,
      where('isActive', '==', true),
      orderBy('savedAt', 'desc'),
      limit(100)
    );
    
    const snapshot = await getDocs(q);
    const memories = [];

    snapshot.forEach(doc => {
      const data = doc.data();
      const memoryText = data.memory.toLowerCase();
      
      if (memoryText.includes(searchTerm.toLowerCase())) {
        memories.push({
          id: doc.id,
          ...data
        });
      }
    });

    return memories.slice(0, 5);

  } catch (error) {
    console.error('❌ Basic search failed:', error);
    return [];
  }
}

/**
 * Extract conversation context
 */
async extractConversationContext(messages, targetMemory, azureConfig) {
  if (!messages || messages.length === 0) {
    return {
      topics: [],
      relatedConcepts: [],
      conversationSummary: ''
    };
  }

  try {
    const recentMessages = messages
      .filter(m => m.content && m.content.trim())
      .slice(-10)
      .map(m => `${m.type === 'user' ? 'User' : 'AI'}: ${m.content}`)
      .join('\n');

    if (!azureConfig?.endpoint || !azureConfig?.apiKey) {
      return {
        topics: ['general conversation'],
        relatedConcepts: [],
        conversationSummary: 'General discussion'
      };
    }

    const response = await fetch(
      `${azureConfig.endpoint}/openai/deployments/${azureConfig.deployment}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': azureConfig.apiKey
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `Extract conversation context for this memory: "${targetMemory}"

Return ONLY valid JSON:
{
  "topics": ["topic1", "topic2"],
  "relatedConcepts": ["concept1", "concept2"],
  "conversationSummary": "Brief summary"
}`
            },
            {
              role: 'user',
              content: `Memory: "${targetMemory}"\n\nConversation:\n${recentMessages}`
            }
          ],
          temperature: 0.3,
          max_tokens: 200
        })
      }
    );

    if (response.ok) {
      const data = await response.json();
      const content = data.choices[0].message.content.trim();
      
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    }

    return {
      topics: ['general'],
      relatedConcepts: [],
      conversationSummary: ''
    };

  } catch (error) {
    console.error('❌ Context extraction failed:', error);
    return {
      topics: [],
      relatedConcepts: [],
      conversationSummary: ''
    };
  }
}


formatTimeText(hoursAgo) {
  if (hoursAgo === null) return 'unknown time';
  
  if (hoursAgo < 1) return 'just now';
  if (hoursAgo < 2) return 'about an hour';
  if (hoursAgo < 24) return `${Math.round(hoursAgo)} hours`;
  
  const daysAgo = hoursAgo / 24;
  if (daysAgo < 2) return '1 day';
  if (daysAgo < 7) return `${Math.floor(daysAgo)} days`;
  
  const weeksAgo = daysAgo / 7;
  if (weeksAgo < 4) return `${Math.floor(weeksAgo)} weeks`;
  
  return 'a while';
}

  // ============================================
  // ✅ HELPER METHOD
  // ============================================

  getTimeAgo(timestamp) {
    if (!timestamp) return 'unknown time';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const secondsAgo = Math.floor((now - date) / 1000);
      
      if (secondsAgo < 60) return 'just now';
      
      const minutesAgo = Math.floor(secondsAgo / 60);
      if (minutesAgo < 60) {
        return `${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago`;
      }
      
      const hoursAgo = Math.floor(minutesAgo / 60);
      if (hoursAgo < 24) {
        return `${hoursAgo} hour${hoursAgo !== 1 ? 's' : ''} ago`;
      }
      
      const daysAgo = Math.floor(hoursAgo / 24);
      if (daysAgo < 7) {
        return `${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago`;
      }
      
      const weeksAgo = Math.floor(daysAgo / 7);
      if (weeksAgo < 4) {
        return `${weeksAgo} week${weeksAgo !== 1 ? 's' : ''} ago`;
      }
      
      const monthsAgo = Math.floor(daysAgo / 30);
      return `${monthsAgo} month${monthsAgo !== 1 ? 's' : ''} ago`;
      
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'unknown time';
    }
  }

getHoursAgo(timestamp) {
  if (!timestamp) {
    console.warn('⚠️ getHoursAgo: No timestamp provided');
    return 0;
  }
  
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const msAgo = now - date;
    const hoursAgo = Math.floor(msAgo / (1000 * 60 * 60));
    
    // ============================================
    // FIX: Add validation - return 0 if invalid
    // ============================================
    if (isNaN(hoursAgo) || hoursAgo < 0) {
      console.warn(`⚠️ getHoursAgo: Invalid calculation result: ${hoursAgo}`);
      return 0;
    }
    
    return hoursAgo;
  } catch (e) {
    console.error('Error calculating hours ago:', e);
    return 0;
  }
}



  // ============================================
  // DELETION
  // ============================================

async getAllMemories() {
  if (!this.isReady()) {
    return [];
  }

  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const q = query(
      memoryRef,
      where('isActive', '==', true),
      limit(1) // Just need to know if ANY exist
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
  } catch (error) {
    console.error('❌ Failed to check memory existence:', error);
    return [];
  }
}
  async deleteMemory(memoryId) {
    if (!this.isReady()) {
      throw new Error('Memory service not initialized');
    }

    try {
      const memoryRef = doc(this.db, 'users', this.currentUser.uid, 'conversationMemory', memoryId);
      await deleteDoc(memoryRef);
      
      console.log(`✅ Deleted memory: ${memoryId}`);
      return true;
    } catch (error) {
      console.error('Failed to delete memory:', error);
      return false;
    }
  }
  /**
 * Update an existing memory
 */
async updateMemory(memoryId, updates) {
  if (!this.isReady()) {
    throw new Error('Memory service not initialized');
  }

  try {
    console.log('📝 Updating memory:', memoryId);
    
    const memoryRef = doc(
      this.db,
      'users',
      this.currentUser.uid,
      'conversationMemory',
      memoryId
    );

    // Update the memory document
    await updateDoc(memoryRef, {
      memory: updates.memory,
      updatedAt: serverTimestamp(),
      lastModified: new Date().toISOString()
    });

    console.log('✅ Memory updated successfully');
    return { success: true, memoryId };
  } catch (error) {
    console.error('❌ Failed to update memory:', error);
    throw error;
  }
}
async searchMemoriesByDateRange(startDate, endDate) {
  if (!this.isReady()) {
    return [];
  }

  try {
    const memoryRef = collection(this.db, 'users', this.currentUser.uid, 'conversationMemory');
    
    const q = query(
      memoryRef,
      where('mentionedAt', '>=', Timestamp.fromDate(startDate)),
      where('mentionedAt', '<=', Timestamp.fromDate(endDate)),
      where('isActive', '==', true),
      orderBy('mentionedAt', 'desc'),
      limit(50)
    );

    const snapshot = await getDocs(q);
    const memories = [];

    snapshot.forEach((doc) => {
      memories.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`✅ Found ${memories.length} memories between ${startDate.toDateString()} and ${endDate.toDateString()}`);
    
    return memories;

  } catch (error) {
    console.error('❌ Date range search error:', error);
    return [];
  }
}
/**
 * Extract event date from memory text
 * Finds dates in multiple formats
 */
extractEventDate(memoryText) {
  if (!memoryText) return null;
  
  const patterns = [
    { regex: /(\d{1,2})\/(\d{1,2})\/(\d{4})/, format: 'slash' },       // 12/25/2024
    { regex: /(\d{4})-(\d{1,2})-(\d{1,2})/, format: 'dash' },           // 2024-12-25
    { regex: /(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+at\s+(\d{1,2}):?(\d{2})?\s*(am|pm)?/i, format: 'day' },
    { regex: /in\s+(\d+)\s+weeks?/i, format: 'weeks' },
    { regex: /next\s+(week|monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i, format: 'next' },
    { regex: /tomorrow/i, format: 'tomorrow' },
    { regex: /today/i, format: 'today' }
  ];
  
  for (let pattern of patterns) {
    const match = memoryText.match(pattern.regex);
    if (match) {
      const result = this.parseMemoryDate(match, pattern.format);
      if (result) {
        console.log(`📅 Event date extracted: ${result.toDateString()}`);
        return result;
      }
    }
  }
  return null;
}

/**
 * Parse memory date in multiple formats
 * Converts matched patterns to JS Date objects
 */
parseMemoryDate(match, format) {
  const now = new Date();
  
  // MM/DD/YYYY format
  if (format === 'slash') {
    const date = new Date(match[3], match[1] - 1, match[2]);
    date.setHours(18, 0, 0, 0); // Set to 6 PM if no time specified
    return date;
  }
  
  // YYYY-MM-DD format
  if (format === 'dash') {
    const date = new Date(match[1], match[2] - 1, match[3]);
    date.setHours(18, 0, 0, 0);
    return date;
  }
  
  // "Monday at 3pm" format
  if (format === 'day') {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = match[1].toLowerCase();
    const targetDay = days.indexOf(dayName);
    const current = now.getDay();
    
    let daysAhead = targetDay - current;
    if (daysAhead <= 0) daysAhead += 7; // Next week if day already passed
    
    const result = new Date(now);
    result.setDate(result.getDate() + daysAhead);
    
    // Extract time if provided (match[2] = hour, match[3] = minutes, match[4] = am/pm)
    if (match[2]) {
      let hours = parseInt(match[2]);
      const minutes = match[3] ? parseInt(match[3]) : 0;
      const period = match[4] ? match[4].toLowerCase() : 'pm';
      
      if (period === 'pm' && hours !== 12) {
        hours += 12;
      } else if (period === 'am' && hours === 12) {
        hours = 0;
      }
      
      result.setHours(hours, minutes, 0, 0);
    } else {
      result.setHours(18, 0, 0, 0); // Default to 6 PM
    }
    
    return result;
  }
  
  // "in 3 weeks" format
  if (format === 'weeks') {
    const weeks = parseInt(match[1]);
    const result = new Date(now);
    result.setDate(result.getDate() + (weeks * 7));
    result.setHours(18, 0, 0, 0);
    return result;
  }
  
  // "next monday" or "next week" format
  if (format === 'next') {
    const result = new Date(now);
    
    if (match[1].toLowerCase() === 'week') {
      // Next week (7 days from now)
      result.setDate(result.getDate() + 7);
    } else {
      // Next occurrence of specific day
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = match[1].toLowerCase();
      const targetDay = days.indexOf(dayName);
      const currentDay = result.getDay();
      
      let daysAhead = targetDay - currentDay;
      if (daysAhead <= 0) daysAhead += 7;
      
      result.setDate(result.getDate() + daysAhead);
    }
    
    result.setHours(18, 0, 0, 0);
    return result;
  }
  
  // "tomorrow" format
  if (format === 'tomorrow') {
    const result = new Date(now);
    result.setDate(result.getDate() + 1);
    result.setHours(18, 0, 0, 0);
    return result;
  }
  
  // "today" format
  if (format === 'today') {
    const result = new Date(now);
    result.setHours(23, 59, 59, 0); // End of today
    return result;
  }
  
  return null;
}
/**
   * ============================================
   * HYBRID DETECTION - Fast + LLM (no new files)
   * Add this to ConversationMemoryService
   * ============================================
   */
  async hybridRecallDetection(userMessage) {
    const startTime = performance.now();
    console.log('⚡ Hybrid detection (fast + LLM)...');

    // STEP 1: Quick pattern check (instant <5ms)
    const quickGuess = this.quickPatternGuess(userMessage);
    
    // If confident it's a recall, return immediately
    if (quickGuess.isRecall === true) {
      const elapsed = performance.now() - startTime;
      console.log(`✅ Confident quick guess in ${Math.round(elapsed)}ms`);
      return {
        isRecallQuery: true,
        confidence: 0.85,
        timeframe: quickGuess.timeframe,
        method: 'quick_guess',
        elapsed: Math.round(elapsed)
      };
    }

    // If confident it's NOT a recall, return immediately
    if (quickGuess.isRecall === false) {
      const elapsed = performance.now() - startTime;
      console.log(`✅ Confident NOT a recall in ${Math.round(elapsed)}ms`);
      return {
        isRecallQuery: false,
        confidence: 0.9,
        method: 'quick_guess',
        elapsed: Math.round(elapsed)
      };
    }

    // Unclear - use LLM
    console.log('🤖 Quick guess unclear, using LLM...');
    try {
      const llmResult = await this.detectConversationSummaryRequest(userMessage);
      const elapsed = performance.now() - startTime;
      
      return {
        ...llmResult,
        isRecallQuery: llmResult.isRequestingRecap,
        method: 'llm',
        elapsed: Math.round(elapsed)
      };
    } catch (error) {
      console.error('❌ LLM error:', error);
      return {
        isRecallQuery: false,
        confidence: 0.5,
        method: 'quick_guess_fallback',
        elapsed: Math.round(performance.now() - startTime)
      };
    }
  }

  /**
   * ============================================
   * QUICK PATTERN GUESS (instant <5ms)
   * ============================================
   */
  quickPatternGuess(userMessage) {
    const msg = userMessage.toLowerCase();

    // Pattern 1: Explicit timeframe + memory words
    const timeframePatterns = [
      { regex: /yesterday.*(talk|discuss|mention|said|tell)/i, timeframe: 'yesterday' },
      { regex: /today.*(talk|discuss|mention|said|tell)/i, timeframe: 'today' },
      { regex: /last week.*(talk|discuss|mention|said|tell)/i, timeframe: 'last week' },
      { regex: /this week.*(talk|discuss|mention|said)/i, timeframe: 'this week' },
      { regex: /(\d+)\s*days?\s*ago.*(talk|discuss|mention|said)/i, timeframe: 'days ago' }
    ];

    for (const pattern of timeframePatterns) {
      if (pattern.regex.test(msg)) {
        const match = msg.match(/(\d+)\s*days?\s*ago/);
        const timeframe = match ? `${match[1]} days ago` : pattern.timeframe;
        
        return {
          isRecall: true,
          timeframe: timeframe
        };
      }
    }

    // Pattern 2: Memory keywords
    const memoryWords = ['remember', 'remind', 'recall', 'what did', 'did i', 'talked about', 'discussed'];
    const hasMemoryWord = memoryWords.some(word => msg.includes(word));

    if (hasMemoryWord && msg.length > 10) {
      return {
        isRecall: true,
        timeframe: null
      };
    }

    // Pattern 3: Negative indicators (NOT a recall)
    const notRecall = ['how are you', 'thanks', 'okay', 'sure', 'help me with', 'create', 'make'];
    const hasNegative = notRecall.some(word => msg.includes(word));

    if (hasNegative) {
      return {
        isRecall: false,
        timeframe: null
      };
    }

    // Unclear - need LLM
    return {
      isRecall: null,
      timeframe: null
    };
  }

  /**
   * ============================================
   * PARALLEL MEMORY SEARCH - All at once
   * ============================================
   */
  async parallelSearchMemories(timeframe) {
    console.log('⚡ Starting parallel memory search...');
    const startTime = performance.now();

    try {
      // If specific timeframe, search just that
      if (timeframe) {
        const { startDate, endDate } = this.calculateDateRange(timeframe);
        const memories = await this.searchMemoriesByDateRange(startDate, endDate);
        const elapsed = performance.now() - startTime;
        
        console.log(`✅ Found ${memories.length} memories in ${Math.round(elapsed)}ms`);
        return {
          memories: memories,
          timeframe: timeframe,
          elapsed: Math.round(elapsed)
        };
      }

      // No timeframe - search 3 timeframes IN PARALLEL
      const [recent, weekly, allTime] = await Promise.all([
        this.getRecentMemories(20, 3),
        this.getRecentMemories(30, 7),
        this.getRecentMemories(50, 365)
      ]);

      const elapsed = performance.now() - startTime;
      console.log(`⚡ Parallel search complete in ${Math.round(elapsed)}ms`);

      return {
        recent: recent,
        weekly: weekly,
        allTime: allTime,
        elapsed: Math.round(elapsed)
      };

    } catch (error) {
      console.error('❌ Parallel search error:', error);
      return { recent: [], weekly: [], allTime: [], elapsed: 0 };
    }
  }

  /**
   * ============================================
   * FAST MEMORY RECALL - Main fast method
   * Use this instead of detectConversationSummaryRequest
   * ============================================
   */
  async fastMemoryRecall(userMessage) {
    console.log('\n🚀 FAST MEMORY RECALL 🚀');
    const totalStart = performance.now();

    try {
      // STEP 1: Hybrid detection (fast + LLM fallback)
      const detection = await this.hybridRecallDetection(userMessage);

      // STEP 2: If NOT a recall, return immediately
      if (!detection.isRecallQuery) {
        console.log('⏭️ Not a recall query\n');
        return { isRecallQuery: false };
      }

      // STEP 3: Parallel memory search
      const searchResult = await this.parallelSearchMemories(detection.timeframe);

      // STEP 4: Build final result
      const totalElapsed = Math.round(performance.now() - totalStart);
      
      const result = {
        isRecallQuery: true,
        timeframe: detection.timeframe,
        confidence: detection.confidence,
        memories: this.selectBestMemories(detection.timeframe, searchResult),
        timing: {
          detection: detection.elapsed,
          search: searchResult.elapsed,
          total: totalElapsed
        },
        method: detection.method
      };

      console.log(`✅ COMPLETE in ${totalElapsed}ms (${detection.method})\n`);
      
      return result;

    } catch (error) {
      console.error('❌ Fast recall error:', error);
      return { isRecallQuery: false, error: error.message };
    }
  }

  /**
   * ============================================
   * SELECT BEST MEMORIES based on timeframe
   * ============================================
   */
  selectBestMemories(timeframe, searchResult) {
    if (!timeframe) {
      // No specific timeframe - return all time
      return searchResult.allTime || [];
    }

    if (timeframe === 'yesterday' || timeframe === 'today') {
      return searchResult.memories || searchResult.recent || [];
    }

    if (timeframe === 'this week' || timeframe === 'last week') {
      return searchResult.memories || searchResult.weekly || [];
    }

    if (timeframe?.includes('days ago')) {
      return searchResult.memories || searchResult.recent || [];
    }

    return searchResult.allTime || searchResult.memories || [];
  }
}

export default ConversationMemoryService;